/*============================================================================
  HC - IB collection filters v2 controller - 2026-09-23

  Drives snippets/ib-filters.liquid on collection and search pages. Loaded from
  layout/theme.liquid only when settings.collection_filters_v2 is on, after
  theme.js and collection-atc.js.

  What it does:
  - Desktop popovers apply instantly. The grid is re-rendered through the
    Section Rendering API (?section_id=<main section>) and swapped in place;
    an open popover is never replaced, its rows are patched by key so search
    text, scroll and focus survive. One history entry per popover session.
  - The phone Filters sheet is staged: picks only touch the sheet, a debounced
    request to sections/ib-filters-facets.liquid refreshes its counts and the
    "Show N products" button, and one commit applies everything when the
    shopper taps it. The desktop "All filters" panel and the Brand drawer apply
    every pick instantly and patch their rows in place.
  - Every request guards its response: anything that is not our markup (a
    Cloudflare/Shopify challenge page, a 5xx) falls back to a full navigation
    to the same URL, which shows the challenge and lets it be solved.
  - "Show more" appends the next page in place, by itself for the first two
    pages of a result set. The button asks for its page on intent (a mouse
    resting on it, a press, keyboard focus), so a tap usually finds it there.
  - After each swap it re-runs what the grid cards need: CollectionATC.rebind
    (quick-add steppers, painted from its cart state), swatch hover on the new
    cards, theme.reinitProductGridItem (reviews, collapsibles). Never
    theme.sections.reinit: it re-inits every collection section on the page
    and stacks their QuickAdd click handlers.
  - Stock theme.Collection never touches this markup: the v2 root drops
    data-collection-template and uses none of the stock filter hooks.

  URL state only ever changes filter.*, sort_by and page; q, type and
  options[prefix] on search pass through untouched.

  Everything the tests need is exported on window.IBFilters. ES5 on purpose
  (no build step).
==============================================================================*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';
  var REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  var LOADING_DELAY_MS = 120;
  var FACETS_DEBOUNCE_MS = 250;
  var SHEET_CLOSE_MS = 280;
  // A render or Load more still pending after this falls back to loading the
  // page, so a stalled request never leaves the grid dimmed and unclickable.
  var RENDER_TIMEOUT_MS = 15000;
  // Scrolling near the end of the grid loads the next page by itself this
  // many times per result set; after that the shopper taps the button, so
  // Recently viewed and the footer stay reachable on a big collection.
  var AUTO_MORE_PAGES = 2;
  // Starts the load this many screens before the button comes into view. In
  // pixels: IntersectionObserver resolves a percentage bottom margin against
  // the viewport's width, a short distance on a portrait phone.
  var AUTO_MORE_SCREENS = 1.5;
  // The button starts loading its page on intent: a mouse or pen resting on
  // it this long (a pointer passing over it asks for nothing), or at once on
  // a press or keyboard focus. The page waits this long for its tap before a
  // later intent asks for it again.
  var MORE_HOVER_MS = 80;
  var MORE_PREFETCH_TTL_MS = 60 * 1000;
  var FACETS_SECTION = 'ib-filters-facets';
  var RESULTS_MARKER = 'data-ib-f-results';
  var SWAP_REGIONS = ['badge', 'rail', 'count', 'sortlabel', 'chips', 'sortlist', 'sheet', 'apply'];
  var FOCUS_KEYS = ['data-ib-f-pill', 'data-ib-f-toggle', 'data-ib-f-sort', 'data-ib-f-view', 'data-ib-f-open'];
  // A render is 250-400 KB of markup, so a phone keeps only the last few.
  var CACHE_LIMIT = 6;
  var CACHE_TTL_MS = 3 * 60 * 1000;
  var SCROLL_TTL_MS = 30 * 60 * 1000;
  var SCROLL_KEY = 'ibf:scroll:';
  var AZ_LETTERS = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  var AZ_MIN_ROWS = 20;
  var AZ_SCROLL_RATIO = 1.2;
  var AZ_SCRUB_SLOP_PX = 6;
  var AZ_HOLD_TAP_MS = 900;
  var AZ_HOLD_SCRUB_MS = 160;
  var SUGGEST_MAX = 6;
  var SWIPE_CLOSE_PX = 110;
  var ENTER_MS = 420;

  /* ------------------------------------------------------------------------
     Pure helpers (exported for tests)
  ------------------------------------------------------------------------ */

  // normalizeText, matchesQuery and letterFor are copies of the ones in
  // assets/ib-mobile-menu.js, which only loads with the mobile menu v2 flag;
  // tests/filters/review3.test.js fails if the copies drift apart.
  // "L'Oreal" -> "loreal", "äz" -> "az", "It's a 10" -> "its a 10".
  function normalizeText(value) {
    var s = value === null || value === undefined ? '' : String(value);
    if (typeof s.normalize === 'function') {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    s = s.toLowerCase();
    s = s.replace(/&amp;/g, ' ');
    s = s.replace(/[^a-z0-9 ]+/g, '');
    s = s.replace(/\s+/g, ' ').replace(/^ | $/g, '');
    return s;
  }

  // Both arguments are already normalized. Empty query matches everything.
  function matchesQuery(normTitle, normQuery) {
    var title = String(normTitle || '');
    var query = String(normQuery || '');
    if (!query) return true;
    if (title.indexOf(query) !== -1) return true;
    var squashedTitle = title.replace(/ /g, '');
    var squashedQuery = query.replace(/ /g, '');
    if (squashedQuery && squashedTitle.indexOf(squashedQuery) !== -1) return true;
    var words = title.split(' ');
    for (var i = 0; i < words.length; i++) {
      if (words[i].indexOf(query) === 0) return true;
    }
    return false;
  }

  function isFilterKey(key) {
    return String(key).indexOf('filter.') === 0;
  }

  function entriesOf(params) {
    var out = [];
    params.forEach(function (value, key) {
      out.push([key, value]);
    });
    return out;
  }

  function fromEntries(entries) {
    var params = new URLSearchParams();
    entries.forEach(function (entry) {
      params.append(entry[0], entry[1]);
    });
    return params;
  }

  function paramsFromUrl(url) {
    var s = String(url || '');
    var hash = s.indexOf('#');
    if (hash !== -1) s = s.slice(0, hash);
    var q = s.indexOf('?');
    var params = new URLSearchParams(q === -1 ? '' : s.slice(q + 1));
    params.delete('section_id');
    return params;
  }

  function buildUrl(pathname, params) {
    var qs = params.toString();
    return pathname + (qs ? '?' + qs : '');
  }

  // Request URL for a section render. Keys are sorted (values keep their
  // order) so the same state always makes the same URL, which is what the
  // response cache keys on; Shopify does not care about parameter order.
  function sectionUrl(pathname, params, sectionId) {
    var entries = entriesOf(params).filter(function (entry) {
      return entry[0] !== 'section_id';
    });
    entries = entries
      .map(function (entry, index) {
        return { entry: entry, index: index };
      })
      .sort(function (a, b) {
        if (a.entry[0] === b.entry[0]) return a.index - b.index;
        return a.entry[0] < b.entry[0] ? -1 : 1;
      })
      .map(function (item) {
        return item.entry;
      });
    var p = fromEntries(entries);
    p.append('section_id', sectionId);
    return pathname + '?' + p.toString();
  }

  // Drop every value of `names` (and the page), then append `entries`.
  function replaceGroup(params, names, entries) {
    var drop = {};
    (names || []).forEach(function (name) {
      drop[name] = true;
    });
    var next = fromEntries(entriesOf(params).filter(function (entry) {
      return entry[0] !== 'page' && !drop[entry[0]];
    }));
    (entries || []).forEach(function (entry) {
      if (entry && entry.name) next.append(entry.name, entry.value);
    });
    return next;
  }

  function entryId(name, value) {
    return name + '\u0000' + value;
  }

  // Remove exact name=value pairs (a chip, an untoggled switch).
  function removeEntries(params, entries) {
    var kill = (entries || []).map(function (entry) {
      return entryId(entry.name, entry.value);
    });
    return fromEntries(entriesOf(params).filter(function (entry) {
      return entry[0] !== 'page' && kill.indexOf(entryId(entry[0], entry[1])) === -1;
    }));
  }

  // Add name=value pairs that are not there yet.
  function appendEntries(params, entries) {
    var list = entriesOf(params).filter(function (entry) {
      return entry[0] !== 'page';
    });
    var have = list.map(function (entry) {
      return entryId(entry[0], entry[1]);
    });
    (entries || []).forEach(function (entry) {
      if (!entry || !entry.name) return;
      var id = entryId(entry.name, entry.value);
      if (have.indexOf(id) !== -1) return;
      have.push(id);
      list.push([entry.name, entry.value]);
    });
    return fromEntries(list);
  }

  // Keeps q, type, options[prefix], sort_by and anything else that is not ours.
  function clearAll(params) {
    return fromEntries(entriesOf(params).filter(function (entry) {
      return !isFilterKey(entry[0]) && entry[0] !== 'page';
    }));
  }

  function setSort(params, value) {
    var next = fromEntries(entriesOf(params).filter(function (entry) {
      return entry[0] !== 'page';
    }));
    if (value) next.set('sort_by', value);
    else next.delete('sort_by');
    return next;
  }

  function cleanPrice(raw) {
    var s = String(raw === null || raw === undefined ? '' : raw).replace(',', '.').replace(/^\s+|\s+$/g, '');
    if (s === '') return null;
    var n = parseFloat(s);
    if (!isFinite(n) || n < 0) return null;
    return Math.round(n * 100) / 100;
  }

  function formatAmount(n) {
    return String(Math.round(n * 100) / 100);
  }

  // What two typed bounds really mean: a reversed pair is swapped, and a
  // blank, junk or edge-of-range bound is no bound at all (null). The URL and
  // the visible fields both come from this, so they always agree.
  // keepHi is the max already in the URL. The snippet widens the slider to
  // it when it sits above today's top price, so it is never tidied away:
  // other filters can bring dearer products back.
  function priceBounds(lo, hi, rangeMax, keepHi) {
    var a = cleanPrice(lo);
    var b = cleanPrice(hi);
    if (a !== null && b !== null && a > b) {
      var swap = a;
      a = b;
      b = swap;
    }
    if (a !== null && a <= 0) a = null;
    if (b !== null && rangeMax !== null && rangeMax !== undefined && b >= rangeMax && b !== cleanPrice(keepHi)) b = null;
    return { lo: a, hi: b };
  }

  function priceEntries(minName, maxName, lo, hi, rangeMax, keepHi) {
    var bounds = priceBounds(lo, hi, rangeMax, keepHi);
    var entries = [];
    if (bounds.lo !== null) entries.push({ name: minName, value: formatAmount(bounds.lo) });
    if (bounds.hi !== null) entries.push({ name: maxName, value: formatAmount(bounds.hi) });
    return entries;
  }

  function setPrice(params, minName, maxName, lo, hi, rangeMax) {
    return replaceGroup(params, [minName, maxName], priceEntries(minName, maxName, lo, hi, rangeMax, params.get(maxName)));
  }

  /*
   * The phone sheet's draft: the live state with only what the sheet shows
   * swapped for what is picked in it. `shown` are the exact name=value pairs
   * the sheet has a control for, `names` whole groups it owns (price), and
   * `picked` what is ticked. A filter the sheet cannot show (a guest's price
   * range, a value Shopify stopped listing) passes through untouched.
   */
  function draftFrom(base, shown, picked, names) {
    var dropName = {};
    (names || []).forEach(function (name) {
      dropName[name] = true;
    });
    var dropPair = {};
    (shown || []).forEach(function (entry) {
      dropPair[entryId(entry.name, entry.value)] = true;
    });
    var next = fromEntries(entriesOf(base).filter(function (entry) {
      return entry[0] !== 'page' && !dropName[entry[0]] && !dropPair[entryId(entry[0], entry[1])];
    }));
    return appendEntries(next, picked);
  }

  // Raw filter values in the URL; a price range counts once.
  function countActive(params) {
    var n = 0;
    var price = false;
    params.forEach(function (value, key) {
      if (!isFilterKey(key) || value === '') return;
      if (/\.(gte|lte)$/.test(key)) {
        price = true;
        return;
      }
      n += 1;
    });
    return n + (price ? 1 : 0);
  }

  function sameParams(a, b) {
    return a.toString() === b.toString();
  }

  // One history entry per popover or drawer session: the first change pushes,
  // the rest replace. Everything else pushes; back/forward never writes.
  function historyMode(source, session) {
    if (source === 'popstate') return 'none';
    if (session) return session.pushed ? 'replace' : 'push';
    return 'push';
  }

  // Same bucketing as the mobile menu's brand index: a-z by initial, else '#'.
  // Takes a normalizeText() key.
  function letterFor(key) {
    var c = String(key || '').charAt(0);
    return /[a-z]/.test(c) ? c.toUpperCase() : '#';
  }

  /*
   * What to offer removing on a zero-result page: the chips the server
   * rendered, then whatever else the URL carries. Shopify reports no filter
   * values at all when the URL alone yields nothing, so there the chips are at
   * most the price range. A price range is one candidate; every other value is
   * its own. `labelOf(name, value)` names a URL value (the raw value is the
   * last resort: "1" means In stock).
   */
  function suggestionCandidates(chips, params, priceLabel, labelOf) {
    var out = (chips || []).slice();
    var covered = {};
    // Prices compare as numbers: the chip carries 10.5, an old link 10.50.
    var coverKey = function (name, value) {
      return /\.(gte|lte)$/.test(name) ? entryId(name, String(cleanPrice(value))) : entryId(name, value);
    };
    out.forEach(function (chip) {
      chip.entries.forEach(function (entry) {
        covered[coverKey(entry.name, entry.value)] = true;
      });
    });
    var byKey = {};
    var price = [];
    entriesOf(params).forEach(function (entry) {
      if (!isFilterKey(entry[0]) || entry[1] === '' || covered[coverKey(entry[0], entry[1])]) return;
      if (/\.(gte|lte)$/.test(entry[0])) {
        price.push({ name: entry[0], value: entry[1] });
        return;
      }
      // "Kevin Murphy" and "KEVIN MURPHY" are one row in the UI, so one suggestion.
      var key = entry[0] + '|' + normalizeText(entry[1]).replace(/ /g, '');
      if (byKey[key]) {
        byKey[key].entries.push({ name: entry[0], value: entry[1] });
        return;
      }
      var label = (labelOf && labelOf(entry[0], entry[1])) || entry[1];
      byKey[key] = { label: label, entries: [{ name: entry[0], value: entry[1] }], price: false };
      out.push(byKey[key]);
    });
    if (price.length) out.push({ label: priceLabel, entries: price, price: true });
    return out;
  }

  // Zero-result suggestions: drop the ones that would still be empty, biggest first.
  function rankSuggestions(list) {
    return (list || [])
      .filter(function (item) {
        return item && item.total > 0;
      })
      .sort(function (a, b) {
        return b.total - a.total;
      });
  }

  /*
   * A small LRU of rendered sections keyed by request URL, so back/forward and
   * re-toggling a filter render instantly. Only markup that passed the marker
   * check is stored, so a challenge page is never replayed. Quick-add
   * quantities in cached cards re-sync through CollectionATC.rebind.
   */
  function createCache(limit, ttlMs, now) {
    var clock = now || function () {
      return Date.now();
    };
    var keys = [];
    var store = {};
    var has = function (key) {
      return Object.prototype.hasOwnProperty.call(store, key);
    };
    var drop = function (key) {
      if (!has(key)) return;
      delete store[key];
      keys.splice(keys.indexOf(key), 1);
    };
    return {
      get: function (key) {
        if (!has(key)) return null;
        if (clock() - store[key].t > ttlMs) {
          drop(key);
          return null;
        }
        keys.splice(keys.indexOf(key), 1);
        keys.push(key);
        return store[key].html;
      },
      set: function (key, html) {
        drop(key);
        store[key] = { html: html, t: clock() };
        keys.push(key);
        while (keys.length > limit) drop(keys[0]);
      },
      drop: drop,
      size: function () {
        return keys.length;
      }
    };
  }

  /*
   * The request pipeline, DOM-free so it can be tested. The latest request
   * always wins: an older response that lands late is dropped as 'stale'.
   * Anything that is not our markup falls back to a full navigation.
   */
  function createRenderer(opts) {
    var seq = 0;
    var inflight = null;
    var clock = opts.now || function () {
      return Date.now();
    };

    function settle(outcome, meta) {
      if (opts.onSettle) opts.onSettle(outcome, meta);
      return outcome;
    }

    function run(url, fetchUrl, meta) {
      var id = ++seq;
      if (inflight && typeof inflight.abort === 'function') inflight.abort();
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      inflight = ctrl;
      meta = meta || {};
      meta.t0 = clock();

      var request;
      var timer = null;
      var timedOut = false;
      var cached = opts.cache ? opts.cache.get(fetchUrl) : null;
      meta.cached = Boolean(cached);
      if (cached) {
        request = Promise.resolve({
          ok: true,
          status: 200,
          text: function () {
            return Promise.resolve(cached);
          }
        });
      } else {
        try {
          request = opts.fetch(fetchUrl, {
            credentials: 'same-origin',
            signal: ctrl ? ctrl.signal : undefined
          });
          if (ctrl) {
            timer = setTimeout(function () {
              timedOut = true;
              ctrl.abort();
            }, RENDER_TIMEOUT_MS);
          }
        } catch (err) {
          request = Promise.reject(err);
        }
      }
      var done = function (outcome) {
        clearTimeout(timer);
        return settle(outcome, meta);
      };

      return Promise.resolve(request)
        .then(function (res) {
          if (id !== seq) return { stale: true };
          if (!res || !res.ok) throw new Error('ib-filters: HTTP ' + (res && res.status));
          return res.text().then(function (html) {
            return { html: html };
          });
        })
        .then(function (result) {
          if (result.stale || id !== seq) return done('stale');
          if (!result.html || result.html.indexOf(opts.marker) === -1) {
            opts.navigate(url, meta);
            return done('navigated');
          }
          meta.ms = clock() - meta.t0;
          opts.apply(result.html, url, meta);
          if (opts.cache && !meta.cached) opts.cache.set(fetchUrl, result.html);
          return done('applied');
        })
        .catch(function (err) {
          // An abort is a newer render taking over, unless it was the timeout.
          if (id !== seq || (err && err.name === 'AbortError' && !timedOut)) return done('stale');
          opts.navigate(url, meta);
          return done('navigated');
        });
    }

    // Teardown: whatever is still in flight settles as 'stale' and touches
    // nothing (no swap, no history entry, no navigation).
    function dispose() {
      seq += 1;
      if (inflight && typeof inflight.abort === 'function') inflight.abort();
      inflight = null;
    }

    return {
      run: run,
      dispose: dispose,
      seq: function () {
        return seq;
      }
    };
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function findByAttr(scope, selector, attr, value) {
    if (!scope) return null;
    var nodes = toArray(scope.querySelectorAll(selector));
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute(attr) === value) return nodes[i];
    }
    return null;
  }

  function syncRow(li, next, opts) {
    var input = li.querySelector('[data-ib-f-opt]');
    var nextInput = next.querySelector('[data-ib-f-opt]');
    if (opts.syncChecked && input && nextInput) input.checked = nextInput.hasAttribute('checked');

    var useless = next.classList.contains('is-useless');
    if (useless && input && input.checked) useless = false;
    li.classList.toggle('is-useless', useless);

    var n = li.querySelector('[data-ib-f-n]');
    var nextN = next.querySelector('[data-ib-f-n]');
    if (n && nextN && n.textContent !== nextN.textContent) n.textContent = nextN.textContent;

    // A merged row only ever gains underlying values. Other filters can drop
    // one variant's count to 0 (so the server stops listing it), but ticking
    // "Kevin Murphy" must keep meaning both spellings once those filters go.
    var have = toArray(li.querySelectorAll('[data-ib-f-value]')).map(function (el) {
      return el.getAttribute('name') + '\u0000' + el.getAttribute('value');
    });
    toArray(next.querySelectorAll('[data-ib-f-value]')).forEach(function (el) {
      var id = el.getAttribute('name') + '\u0000' + el.getAttribute('value');
      if (have.indexOf(id) !== -1) return;
      have.push(id);
      li.appendChild(li.ownerDocument.importNode(el, true));
    });
  }

  /*
   * Keyed patch of one option list. Existing rows are updated in place and
   * never moved (moving a focused node blurs it); rows the server added are
   * inserted after their predecessor; rows the server dropped fall to count 0
   * and hide unless they are ticked.
   */
  function patchList(list, nextList, opts) {
    if (!list || !nextList) return;
    opts = opts || {};
    var current = {};
    toArray(list.children).forEach(function (li) {
      var key = li.getAttribute('data-ib-f-key');
      if (key !== null) current[key] = li;
    });

    var prev = null;
    toArray(nextList.children).forEach(function (next) {
      var key = next.getAttribute('data-ib-f-key');
      var li = Object.prototype.hasOwnProperty.call(current, key) ? current[key] : null;
      if (li) {
        delete current[key];
        syncRow(li, next, opts);
      } else {
        li = list.ownerDocument.importNode(next, true);
        list.insertBefore(li, prev ? prev.nextSibling : list.firstChild);
        if (opts.onInsert) opts.onInsert(li);
      }
      prev = li;
    });

    Object.keys(current).forEach(function (key) {
      var li = current[key];
      var input = li.querySelector('[data-ib-f-opt]');
      var n = li.querySelector('[data-ib-f-n]');
      if (n) n.textContent = '0';
      if (!(input && input.checked)) li.classList.add('is-useless');
    });
  }

  /* ------------------------------------------------------------------------
     Controller
  ------------------------------------------------------------------------ */

  function matchMediaSafe(query) {
    return window.matchMedia ? window.matchMedia(query) : { matches: false };
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function focusQuietly(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (err) {
      el.focus();
    }
  }

  function raf(fn) {
    return (window.requestAnimationFrame || function (cb) {
      return setTimeout(cb, 16);
    })(fn);
  }

  // For theme calls that return a promise (a cart write, CollectionATC's
  // init): a rejection is theirs to report, and must not surface here as
  // an unhandled rejection. try/catch around the call only sees a throw.
  function quiet(result) {
    if (result && typeof result.then === 'function') result.then(null, function () {});
    return result;
  }

  function valuesOf(scope) {
    return toArray(scope.querySelectorAll('[data-ib-f-value]')).map(function (input) {
      return { name: input.getAttribute('name'), value: input.value };
    });
  }

  // Two controls carrying exactly the same name=value pairs.
  function sameValues(a, b) {
    var ids = function (el) {
      return valuesOf(el).map(function (entry) {
        return entryId(entry.name, entry.value);
      }).sort().join('\u0001');
    };
    return ids(a) === ids(b);
  }

  function namesOf(entries, extra) {
    var names = (extra || []).slice();
    entries.forEach(function (entry) {
      if (names.indexOf(entry.name) === -1) names.push(entry.name);
    });
    return names;
  }

  // theme.Collection's setActiveColorImage, kept to one card.
  function showSwatchImage(swatch) {
    var card = swatch.closest('.grid-item');
    var image = card && card.querySelector('.grid-product__color-image--' + swatch.getAttribute('data-variant-id'));
    if (!image) return;
    toArray(card.querySelectorAll('.grid-product__color-image, .color-swatch--with-image')).forEach(function (el) {
      el.classList.remove('is-active');
    });
    image.style.backgroundImage = 'url(' + swatch.getAttribute('data-variant-image') + ')';
    image.classList.add('is-active');
    swatch.classList.add('is-active');
    var link = swatch.closest('.grid-item__link');
    var url = swatch.getAttribute('data-url');
    if (link && url) link.setAttribute('href', url);
  }

  function Filters(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.page = root.closest('.ib-f-page') || root.parentNode;
    this.bar = root.querySelector('[data-ib-f-bar]');
    this.inner = root.querySelector('.ib-f-bar__inner');
    this.rail = root.querySelector('[data-ib-f-rail]');
    this.view = null;
    this.top = -1;
    this.openPopEl = null;
    this.openPopKey = null;
    this.sortOpen = false;
    this.popSession = { pushed: false };
    this.sheetSession = { pushed: false };
    this.path = window.location.pathname;
    this.committed = paramsFromUrl(window.location.href);
    this.latest = this.committed;
    this.draftRev = 0;
    this.facetsTimer = null;
    this.facetsCtrl = null;
    this.loadingTimer = null;
    this.sheetSnapshot = null;
    this.sheetStaged = null;
    this.sheetCleared = false;
    this.lastFocus = null;
    this.mobile = matchMediaSafe(MOBILE_QUERY);
    this.reduced = matchMediaSafe(REDUCED_QUERY);
    this.listeners = [];
    this.blocked = null;
    this.labels = {};
    this.scrollModeWas = null;
    this.moreReq = null;
    this.morePrefetch = null;
    this.moreHover = null;
    this.moreHoverTimer = null;
    this.moreObserver = null;
    this.autoMoreOff = false;
    this.restoringPages = false;
    this.resultsRev = 0;
    this.ownResize = false;
    this.suggestRev = 0;
    this.suggestCtrl = null;
    this.azHoldUntil = 0;
    this.pagesLoaded = this.currentPage();
    this.firstPage = this.pagesLoaded;
    this.cache = createCache(CACHE_LIMIT, CACHE_TTL_MS);

    var self = this;
    this.renderer = createRenderer({
      fetch: function (url, init) {
        return window.fetch(url, init);
      },
      cache: this.cache,
      marker: RESULTS_MARKER,
      navigate: function (url, meta) {
        self.navigate(url, Boolean(meta) && historyMode(meta.source, meta.session) !== 'push');
      },
      apply: function (html, url, meta) {
        self.apply(html, url, meta);
      },
      onSettle: function (outcome, meta) {
        if (outcome !== 'stale') self.setLoading(false);
        if (outcome === 'applied') self.trackApply(meta.source, meta.ms, meta.cached === true);
      }
    });

    this.bind();
    this.updateTop();
    this.syncStuck();
    this.paintAllRanges();
    this.updateRailFades();
    this.buildAzRails();
    this.initRegions();
    this.fillSuggestions();
    this.restoreScroll();
    this.watchMore();

    try {
      this.stampEntry({});
    } catch (err) {
      /* history is best effort */
    }

    root.setAttribute('data-ib-f-ready', '');
  }

  Filters.prototype = {
    /* ---------------- wiring ---------------- */

    listen: function (target, type, fn, opts) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(type, fn, opts);
      this.listeners.push([target, type, fn, opts]);
    },

    bind: function () {
      var self = this;
      this.listen(this.root, 'click', function (evt) {
        self.onClick(evt);
      });
      this.listen(this.root, 'change', function (evt) {
        self.onChange(evt);
      });
      this.listen(this.root, 'input', function (evt) {
        self.onInput(evt);
      });
      this.listen(this.root, 'keydown', function (evt) {
        self.onKeydown(evt);
      });

      // "Show more" starts loading its page on intent (prefetchMore): at
      // once on a press or keyboard focus; for a mouse or pen once it has
      // rested on the button. Touch hover is left to the press after it.
      var moreLink = function (evt) {
        var t = evt.target;
        return t && t.closest ? t.closest('[data-ib-f-more-link]') : null;
      };
      var warmMore = function (evt) {
        var link = moreLink(evt);
        if (link) self.prefetchMore(link);
      };
      this.listen(this.root, 'pointerdown', warmMore);
      this.listen(this.root, 'focusin', warmMore);
      this.listen(this.root, 'pointerover', function (evt) {
        var link = moreLink(evt);
        if (link && evt.pointerType !== 'touch') self.hoverMore(link);
      });
      this.listen(this.root, 'pointerout', function (evt) {
        var link = moreLink(evt);
        if (!link || link !== self.moreHover) return;
        // Onto the button's own text is still on the button.
        if (evt.relatedTarget && link.contains(evt.relatedTarget)) return;
        self.endMoreHover();
      });

      toArray(this.root.querySelectorAll('[data-ib-f-sheet]')).forEach(function (dialog) {
        self.listen(dialog, 'cancel', function (evt) {
          evt.preventDefault();
          self.closeSheet(dialog, { restore: true });
        });
        self.bindSwipe(dialog);
      });

      this.listen(window, 'pagehide', function () {
        self.saveScroll();
      });

      this.bindAzScrub();
      toArray(this.root.querySelectorAll('[data-ib-f-sheet-body]')).forEach(function (body) {
        var queued = false;
        self.listen(body, 'scroll', function () {
          if (queued) return;
          queued = true;
          raf(function () {
            queued = false;
            // A jump's own scroll must not repaint the rail: on a short list
            // the tapped letter can't reach the top, so the spy would light the
            // top row's letter instead of the one the shopper chose.
            if (Date.now() < self.azHoldUntil) return;
            toArray(body.querySelectorAll('.has-az')).forEach(function (group) {
              self.syncAzCurrent(group);
            });
          });
        }, { passive: true });
      });
      // toggle does not bubble; capture it so opening the Brand accordion can
      // decide whether its list is long enough for a rail.
      this.listen(this.root, 'toggle', function (evt) {
        if (evt.target && evt.target.open && evt.target.matches && evt.target.matches('[data-ib-f-acc]')) {
          raf(function () {
            self.buildAzRails();
          });
        }
      }, true);

      this.listen(document, 'pointerdown', function (evt) {
        self.onOutsidePointer(evt);
      });
      this.listen(document, 'keydown', function (evt) {
        if (evt.key === 'Escape' && (self.openPopEl || self.sortOpen) && !self.root.contains(evt.target)) {
          self.closePop(false);
          self.closeSortPop(false);
        }
      });
      this.listen(window, 'popstate', function (evt) {
        self.onPopState(evt);
      });

      var scheduled = false;
      var onFrame = function () {
        scheduled = false;
        self.updateTop();
        self.syncStuck();
        self.positionPop();
        self.positionSortPop();
      };
      var onScrollish = function () {
        if (scheduled) return;
        scheduled = true;
        raf(onFrame);
      };
      this.listen(window, 'scroll', onScrollish, { passive: true });
      // Phones fire resize for the keyboard and the URL bar; one pass a frame.
      var resizeQueued = false;
      this.listen(window, 'resize', function () {
        if (self.ownResize || resizeQueued) return;
        resizeQueued = true;
        raf(function () {
          resizeQueued = false;
          self.updateTop();
          self.updateRailFades();
          self.buildAzRails();
          onScrollish();
        });
      });
      this.listen(this.rail, 'scroll', function () {
        self.updateRailFades();
        onScrollish();
      }, { passive: true });

      this.listen(document, 'headerStickyChange', function () {
        self.updateTop();
        setTimeout(function () {
          self.updateTop();
        }, 350);
      });

      var headerSection = document.querySelector('.header-section');
      if (headerSection && typeof window.ResizeObserver === 'function') {
        this.headerObserver = new window.ResizeObserver(function () {
          self.updateTop();
        });
        this.headerObserver.observe(headerSection);
      }

      if (this.mobile && typeof this.mobile.addEventListener === 'function') {
        this.listen(this.mobile, 'change', function () {
          self.closePop(false);
          self.closeSortPop(false);
          self.updateRailFades();
        });
      }
    },

    destroy: function () {
      this.listeners.forEach(function (l) {
        l[0].removeEventListener(l[1], l[2], l[3]);
      });
      this.listeners = [];
      if (this.headerObserver) this.headerObserver.disconnect();
      // Nothing that is still in flight may land on a torn-down section.
      this.renderer.dispose();
      this.cancelMore();
      if (this.moreObserver) this.moreObserver.disconnect();
      this.endScrollRestore();
      this.resultsRev += 1;
      this.draftRev += 1;
      this.cancelSuggestions();
      clearTimeout(this.facetsTimer);
      clearTimeout(this.loadingTimer);
      if (this.facetsCtrl) this.facetsCtrl.abort();
      this.root.classList.remove('is-loading');
      this.root.removeAttribute('data-ib-f-ready');
      delete this.root.__ibf;
    },

    isMobile: function () {
      return Boolean(this.mobile && this.mobile.matches);
    },

    /* ---------------- events ---------------- */

    onClick: function (evt) {
      var t = evt.target;
      if (!t || !t.closest) return;
      var el;

      if (t.tagName === 'DIALOG' && t.hasAttribute('data-ib-f-sheet')) {
        this.closeSheet(t, { restore: true });
        return;
      }
      if ((el = t.closest('[data-ib-f-more-link]'))) {
        // A new tab or window (Cmd/Ctrl/Shift/middle click) opens the page as a link.
        if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || (evt.button && evt.button !== 0)) return;
        evt.preventDefault();
        this.loadMore(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-az-letter]'))) {
        if (!this.suppressAzClick) this.jumpToLetter(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-pill]'))) {
        this.togglePop(el.getAttribute('data-ib-f-pill'));
        return;
      }
      if ((el = t.closest('[data-ib-f-open]'))) {
        if (el.getAttribute('data-ib-f-open') === 'filters') this.openSheet('filters');
        else this.onSortButton();
        return;
      }
      if ((el = t.closest('[data-ib-f-toggle]'))) {
        this.onToggle(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-chip]'))) {
        this.onChip(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-clear-all]'))) {
        this.commit(clearAll(this.latest), { source: 'chips' });
        return;
      }
      if ((el = t.closest('[data-ib-f-clear-group]'))) {
        this.onClearGroup(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-sort]'))) {
        this.onSort(el);
        return;
      }
      if ((el = t.closest('[data-ib-f-view]'))) {
        this.setView(el.getAttribute('data-ib-f-view'));
        return;
      }
      if ((el = t.closest('[data-ib-f-close]'))) {
        this.closeSheet(el.closest('dialog'), { restore: true });
        return;
      }
      if ((el = t.closest('[data-ib-f-apply]'))) {
        this.applySheet();
        return;
      }
      if ((el = t.closest('[data-ib-f-sheet-clear]'))) {
        this.clearSheet();
      }
    },

    onChange: function (evt) {
      var t = evt.target;
      if (!t || !t.matches) return;
      var group = t.closest('[data-ib-f-group]');

      if (t.matches('[data-ib-f-opt]')) {
        var row = t.closest('[data-ib-f-key]');
        if (row) this.syncTwins(row, t.checked);
        if (this.isStaged(t)) this.scheduleFacets();
        else if (group && t.closest('[data-ib-f-pop]')) this.commitGroup(group);
        else if (group && this.inSheet(t)) this.commitGroup(group, this.sheetMeta());
        return;
      }
      if (t.matches('[data-ib-f-range-lo], [data-ib-f-range-hi]')) {
        this.syncFieldsFromRange(group, t);
        this.onPriceChange(group, t);
        return;
      }
      if (t.matches('[data-ib-f-price-lo], [data-ib-f-price-hi]')) {
        this.syncRangeFromFields(group);
        this.onPriceChange(group, t);
      }
    },

    onInput: function (evt) {
      var t = evt.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-ib-f-search]')) {
        this.filterGroup(t.closest('[data-ib-f-group]'));
        return;
      }
      if (t.matches('[data-ib-f-range-lo], [data-ib-f-range-hi]')) {
        this.syncFieldsFromRange(t.closest('[data-ib-f-group]'), t);
      }
    },

    onKeydown: function (evt) {
      var t = evt.target;
      if (evt.key === 'Escape') {
        if (t && t.matches && t.matches('[data-ib-f-search]') && t.value) {
          evt.preventDefault();
          evt.stopPropagation();
          t.value = '';
          this.filterGroup(t.closest('[data-ib-f-group]'));
          return;
        }
        if (this.openPopEl && !this.inSheet(t)) {
          evt.preventDefault();
          // Focus goes back to the pill only from inside the popover; from
          // elsewhere in the bar (a chip) it stays where it is.
          this.closePop(this.openPopEl.contains(t));
          return;
        }
        if (this.sortOpen) {
          evt.preventDefault();
          this.closeSortPop(true);
        }
        return;
      }

      if (evt.key === 'Enter' && t && t.matches) {
        if (t.matches('[data-ib-f-price-lo], [data-ib-f-price-hi]')) {
          evt.preventDefault();
          var group = t.closest('[data-ib-f-group]');
          this.syncRangeFromFields(group);
          this.onPriceChange(group, t);
          return;
        }
        if (t.matches('[data-ib-f-search]')) {
          evt.preventDefault();
          return;
        }
      }

      if ((evt.key === 'ArrowDown' || evt.key === 'ArrowUp') && this.openPopEl && this.openPopEl.contains(t)) {
        var items = this.navItems(this.openPopEl);
        if (!items.length) return;
        var index = items.indexOf(t);
        var next = evt.key === 'ArrowDown' ? index + 1 : index - 1;
        if (next < 0) next = 0;
        if (next >= items.length) next = items.length - 1;
        evt.preventDefault();
        focusQuietly(items[next]);
        if (items[next].scrollIntoView) items[next].scrollIntoView({ block: 'nearest' });
      }
    },

    // Where focus goes in a popover or drawer: its first row, or the Min
    // field of a price group (which has no rows). Arrow keys use navItems
    // alone, so the price fields keep their own up and down.
    firstItem: function (scope) {
      return this.navItems(scope)[0] || scope.querySelector('[data-ib-f-price-lo]');
    },

    navItems: function (scope) {
      var items = [];
      var search = scope.querySelector('[data-ib-f-search]');
      if (search) items.push(search);
      toArray(scope.querySelectorAll('[data-ib-f-key]')).forEach(function (li) {
        if (li.classList.contains('is-useless') || li.classList.contains('is-filtered-out') ||
          li.classList.contains('is-region-hidden')) return;
        var input = li.querySelector('[data-ib-f-opt]');
        if (input) items.push(input);
      });
      return items;
    },

    onOutsidePointer: function (evt) {
      var t = evt.target;
      if (this.openPopEl) {
        var pill = this.pillFor(this.openPopKey);
        if (!this.openPopEl.contains(t) && !(pill && pill.contains(t))) this.closePop(false);
      }
      if (this.sortOpen) {
        var pop = this.root.querySelector('[data-ib-f-sortpop]');
        var btn = this.root.querySelector('[data-ib-f-open="sort"]');
        if (pop && !pop.contains(t) && !(btn && btn.contains(t))) this.closeSortPop(false);
      }
    },

    onPopState: function (evt) {
      var params = paramsFromUrl(window.location.href);
      // An entry this controller did not write (a skip link's #MainContent,
      // an anchor in the description) still counts when it is this page
      // with other filters: Back from a filter change can land on it.
      var ours = Boolean(evt.state && evt.state.ibf);
      if (!ours && (window.location.pathname !== this.path || sameParams(params, this.latest))) return;
      this.closePop(false);
      this.closeSortPop(false);
      // Back is a cancel: each open sheet takes its normal close path (a
      // staged draft is discarded, focus goes back, the transition resets).
      // None of that path touches history.
      var self = this;
      toArray(this.root.querySelectorAll('[data-ib-f-sheet]')).forEach(function (dialog) {
        self.closeSheet(dialog, { restore: true });
      });
      // Back over an entry that only moved the hash (a skip link to
      // #MainContent) leaves the filters as they are: re-rendering would drop
      // the pages Load more added and jump to the top.
      if (sameParams(params, this.latest)) return;
      var state = ours ? evt.state : {};
      var restore = typeof state.pages === 'number' && typeof state.y === 'number' ? { pages: state.pages, y: state.y } : null;
      this.commit(params, { source: 'popstate', force: true, restore: restore });
    },

    inSheet: function (el) {
      return Boolean(el.closest('[data-ib-f-sheet]'));
    },

    // Only the phone Filters sheet is staged; every other surface is instant.
    // An open sheet keeps the mode it opened in: turning a tablet across the
    // breakpoint must not turn "Show N products" into a plain close that
    // drops the staged picks.
    isStagedDialog: function (dialog) {
      if (!dialog || dialog.getAttribute('data-ib-f-sheet') !== 'filters') return false;
      if (dialog.open && this.sheetStaged !== null) return this.sheetStaged;
      return this.isMobile();
    },

    isStaged: function (el) {
      return this.isStagedDialog(el && el.closest ? el.closest('[data-ib-f-sheet]') : null);
    },

    sheetMeta: function () {
      return { source: 'sheet', session: this.sheetSession };
    },

    /* ---------------- actions ---------------- */

    commitGroup: function (group, meta) {
      var entries = [];
      toArray(group.querySelectorAll('[data-ib-f-opt]')).forEach(function (input) {
        if (!input.checked) return;
        var row = input.closest('[data-ib-f-key]');
        if (row) entries = entries.concat(valuesOf(row));
      });
      var names = namesOf(valuesOf(group), [group.getAttribute('data-ib-f-group')]);
      this.commit(replaceGroup(this.latest, names, entries), meta || { source: 'pop', session: this.popSession });
    },

    onToggle: function (btn) {
      var on = btn.getAttribute('aria-checked') !== 'true';
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      this.syncTwins(btn, on);
      if (this.isStaged(btn)) {
        this.scheduleFacets();
        return;
      }
      var entries = valuesOf(btn);
      var next = on ? appendEntries(this.latest, entries) : removeEntries(this.latest, entries);
      this.commit(next, this.inSheet(btn) ? this.sheetMeta() : { source: 'toggle' });
    },

    // In the Filters sheet one group can show as a switch and as a list at
    // once: a staged count or a render turns one into the other, and the old
    // one stays up while it holds a pick. Both carry the same values, so they
    // are one selection, and flipping either moves the other. The bar's
    // switches follow the page, not the sheet.
    syncTwins: function (control, on) {
      var sheet = control.closest('[data-ib-f-sheet="filters"]');
      if (!sheet) return;
      var ids = {};
      valuesOf(control).forEach(function (entry) {
        ids[entryId(entry.name, entry.value)] = true;
      });
      var isTwin = function (el) {
        return el !== control && valuesOf(el).some(function (entry) {
          return ids[entryId(entry.name, entry.value)] === true;
        });
      };
      toArray(sheet.querySelectorAll('[data-ib-f-toggle]')).forEach(function (toggle) {
        if (isTwin(toggle)) toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      toArray(sheet.querySelectorAll('[data-ib-f-key]')).forEach(function (row) {
        var input = row.querySelector('[data-ib-f-opt]');
        if (input && isTwin(row)) input.checked = on;
      });
    },

    onChip: function (chip) {
      var entries = valuesOf(chip);
      var next = chip.hasAttribute('data-ib-f-chip-price')
        ? replaceGroup(this.latest, namesOf(entries), [])
        : removeEntries(this.latest, entries);
      this.commit(next, { source: 'chips' });
    },

    onClearGroup: function (btn) {
      var pop = btn.closest('[data-ib-f-pop], [data-ib-f-drawer]');
      var group = pop && pop.querySelector('[data-ib-f-group]');
      if (!group) return;
      var names;
      if (group.getAttribute('data-ib-f-kind') === 'price') {
        names = [group.getAttribute('data-ib-f-min-name'), group.getAttribute('data-ib-f-max-name')];
        this.resetPrice(group);
      } else {
        names = namesOf(valuesOf(group), [group.getAttribute('data-ib-f-group')]);
        toArray(group.querySelectorAll('[data-ib-f-opt]')).forEach(function (input) {
          input.checked = false;
        });
      }
      this.commit(
        replaceGroup(this.latest, names, []),
        pop.hasAttribute('data-ib-f-drawer') ? this.sheetMeta() : { source: 'pop', session: this.popSession }
      );
      var focusTarget = this.firstItem(pop);
      if (focusTarget) focusQuietly(focusTarget);
    },

    onSort: function (btn) {
      var value = btn.getAttribute('data-ib-f-sort');
      toArray(this.root.querySelectorAll('[data-ib-f-sort]')).forEach(function (el) {
        el.setAttribute('aria-pressed', el.getAttribute('data-ib-f-sort') === value ? 'true' : 'false');
      });
      var sheet = btn.closest('[data-ib-f-sheet]');
      if (sheet) this.closeSheet(sheet, { restore: false });
      this.closeSortPop(!sheet);
      this.commit(setSort(this.latest, value), { source: 'sort' });
    },

    onSortButton: function () {
      if (this.isMobile()) {
        this.openSheet('sort');
        return;
      }
      if (this.sortOpen) this.closeSortPop(true);
      else this.openSortPop();
    },

    onPriceChange: function (group, source) {
      if (!group) return;
      if (this.isStaged(source)) {
        this.scheduleFacets();
        return;
      }
      if (source.closest('[data-ib-f-pop]')) {
        this.commit(this.priceParams(this.latest, group), { source: 'pop', session: this.popSession });
      } else if (this.inSheet(source)) {
        this.commit(this.priceParams(this.latest, group), this.sheetMeta());
      }
    },

    setView: function (view) {
      if (!view) return;
      this.view = view;
      toArray(this.root.querySelectorAll('[data-ib-f-view]')).forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-ib-f-view') === view ? 'true' : 'false');
      });
      var grid = this.root.querySelector('[data-ib-f-results] .collection-grid');
      if (grid) grid.setAttribute('data-view', view);
      this.root.setAttribute('data-ib-f-layout', view);
      try {
        if (window.theme && theme.cart && typeof theme.cart.updateAttribute === 'function') {
          quiet(theme.cart.updateAttribute('product_view', view));
        }
      } catch (err) {
        /* the layout still switched; only the remembered preference is lost */
      }
      this.notifyResize();
    },

    /* ---------------- price ---------------- */

    priceParts: function (group) {
      return {
        lo: group.querySelector('[data-ib-f-range-lo]'),
        hi: group.querySelector('[data-ib-f-range-hi]'),
        fieldLo: group.querySelector('[data-ib-f-price-lo]'),
        fieldHi: group.querySelector('[data-ib-f-price-hi]'),
        fill: group.querySelector('[data-ib-f-range-fill]'),
        max: parseFloat(group.getAttribute('data-ib-f-max')) || 0
      };
    },

    priceParams: function (base, group) {
      var p = this.priceParts(group);
      return setPrice(
        base,
        group.getAttribute('data-ib-f-min-name'),
        group.getAttribute('data-ib-f-max-name'),
        p.fieldLo ? p.fieldLo.value : '',
        p.fieldHi ? p.fieldHi.value : '',
        p.max || null
      );
    },

    syncFieldsFromRange: function (group, changed) {
      if (!group) return;
      var p = this.priceParts(group);
      if (!p.lo || !p.hi) return;
      var lo = parseFloat(p.lo.value) || 0;
      var hi = parseFloat(p.hi.value) || 0;
      if (lo > hi) {
        if (changed === p.lo) lo = hi;
        else hi = lo;
        p.lo.value = String(lo);
        p.hi.value = String(hi);
      }
      if (p.fieldLo) p.fieldLo.value = lo > 0 ? String(lo) : '';
      if (p.fieldHi) p.fieldHi.value = hi < p.max || hi === cleanPrice(this.liveMax(group)) ? String(hi) : '';
      this.paintRange(group);
    },

    // Typed bounds are rewritten to what will actually apply (80 and 20
    // become 20 and 80; a max at the top of the range reads as no max), so
    // the fields never disagree with the URL.
    syncRangeFromFields: function (group) {
      if (!group) return;
      var p = this.priceParts(group);
      var bounds = priceBounds(p.fieldLo ? p.fieldLo.value : '', p.fieldHi ? p.fieldHi.value : '', p.max || null, this.liveMax(group));
      if (p.fieldLo) p.fieldLo.value = bounds.lo === null ? '' : formatAmount(bounds.lo);
      if (p.fieldHi) p.fieldHi.value = bounds.hi === null ? '' : formatAmount(bounds.hi);
      if (!p.lo || !p.hi) return;
      p.lo.value = String(bounds.lo === null ? 0 : Math.min(bounds.lo, p.max));
      p.hi.value = String(bounds.hi === null ? p.max : Math.min(bounds.hi, p.max));
      this.paintRange(group);
    },

    // The max in the URL for this price group (none once the phone sheet's
    // Clear all has started a draft from nothing).
    liveMax: function (group) {
      if (this.sheetCleared && this.isStaged(group)) return null;
      return this.latest.get(group.getAttribute('data-ib-f-max-name'));
    },

    resetPrice: function (group) {
      var p = this.priceParts(group);
      if (p.fieldLo) p.fieldLo.value = '';
      if (p.fieldHi) p.fieldHi.value = '';
      if (p.lo) p.lo.value = '0';
      if (p.hi) p.hi.value = String(p.max);
      this.paintRange(group);
    },

    paintRange: function (group) {
      var p = this.priceParts(group);
      if (!p.fill || !p.lo || !p.hi || !p.max) return;
      var lo = Math.max(0, Math.min(parseFloat(p.lo.value) || 0, p.max));
      var hi = Math.max(0, Math.min(parseFloat(p.hi.value) || 0, p.max));
      p.fill.style.left = (lo / p.max) * 100 + '%';
      p.fill.style.right = 100 - (hi / p.max) * 100 + '%';
    },

    paintAllRanges: function () {
      var self = this;
      toArray(this.root.querySelectorAll('[data-ib-f-kind="price"]')).forEach(function (group) {
        self.paintRange(group);
      });
    },

    /* ---------------- search inside a list ---------------- */

    filterGroup: function (group) {
      if (!group) return;
      var input = group.querySelector('[data-ib-f-search]');
      var query = normalizeText(input ? input.value : '');
      var any = false;
      toArray(group.querySelectorAll('[data-ib-f-key]')).forEach(function (li) {
        if (li.__ibfNorm === undefined) li.__ibfNorm = normalizeText(li.getAttribute('data-ib-f-label'));
        var match = !query || matchesQuery(li.__ibfNorm, query);
        li.classList.toggle('is-filtered-out', !match);
        if (match && !li.classList.contains('is-useless') && !li.classList.contains('is-region-hidden')) any = true;
      });
      var nomatch = group.querySelector('[data-ib-f-nomatch]');
      if (nomatch) nomatch.hidden = any || !query;
      group.classList.toggle('is-searching', Boolean(query));
    },

    /* ---------------- popovers ---------------- */

    pillFor: function (key) {
      return key ? findByAttr(this.root, '[data-ib-f-pill]', 'data-ib-f-pill', key) : null;
    },

    togglePop: function (key) {
      var drawer = findByAttr(this.root, '[data-ib-f-drawer]', 'data-ib-f-drawer', key);
      if (drawer && !this.isMobile()) {
        this.openSheet('drawer', drawer);
        return;
      }
      if (this.openPopKey === key) this.closePop(true);
      else this.openPop(key);
    },

    openPop: function (key) {
      this.closeSortPop(false);
      this.closePop(false);
      var pop = findByAttr(this.root.querySelector('[data-ib-f-pops]'), '[data-ib-f-pop]', 'data-ib-f-pop', key);
      if (!pop) return;
      var pill = this.pillFor(key);

      this.openPopEl = pop;
      this.openPopKey = key;
      this.popSession = { pushed: false };

      if (pill && this.rail) {
        var railRect = this.rail.getBoundingClientRect();
        var pillRect = pill.getBoundingClientRect();
        if (pillRect.left < railRect.left) this.rail.scrollLeft -= railRect.left - pillRect.left + 16;
        else if (pillRect.right > railRect.right) this.rail.scrollLeft += pillRect.right - railRect.right + 16;
      }

      pop.hidden = false;
      this.paintAllRanges();
      this.positionPop();
      if (pill) {
        pill.setAttribute('aria-expanded', 'true');
        pill.classList.add('is-open');
      }
      raf(function () {
        pop.classList.add('is-open');
      });
      focusQuietly(this.firstItem(pop) || pop.querySelector('input, button'));
    },

    closePop: function (restoreFocus) {
      var pop = this.openPopEl;
      if (!pop) return;
      var key = this.openPopKey;
      pop.classList.remove('is-open');
      pop.hidden = true;

      var search = pop.querySelector('[data-ib-f-search]');
      if (search && search.value) {
        search.value = '';
        this.filterGroup(search.closest('[data-ib-f-group]'));
      }

      this.openPopEl = null;
      this.openPopKey = null;
      var pill = this.pillFor(key);
      if (pill) {
        pill.setAttribute('aria-expanded', 'false');
        pill.classList.remove('is-open');
      }
      // A popover kept open through a zero-result render has lost its pill;
      // the empty state's Clear all is the way on from there.
      if (restoreFocus) focusQuietly(pill || this.root.querySelector('[data-ib-f-results] [data-ib-f-clear-all]'));
    },

    positionPop: function () {
      var pop = this.openPopEl;
      var pill = this.pillFor(this.openPopKey);
      if (!pop || !pill || !this.inner) return;
      var inner = this.inner.getBoundingClientRect();
      var r = pill.getBoundingClientRect();
      var width = pop.offsetWidth || 340;
      var pad = parseFloat(window.getComputedStyle(this.inner).paddingLeft) || 0;
      var left = r.left - inner.left;
      left = Math.max(pad, Math.min(left, inner.width - pad - width));
      pop.style.left = Math.round(left) + 'px';
      pop.style.top = Math.round(r.bottom - inner.top + 8) + 'px';
      var room = window.innerHeight - r.bottom - 24;
      pop.style.setProperty('--ibf-pop-max', Math.round(Math.max(220, Math.min(room, window.innerHeight * 0.62))) + 'px');
    },

    openSortPop: function () {
      var pop = this.root.querySelector('[data-ib-f-sortpop]');
      var btn = this.root.querySelector('[data-ib-f-open="sort"]');
      if (!pop || !btn) return;
      this.closePop(false);
      this.sortOpen = true;
      pop.hidden = false;
      this.positionSortPop();
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-open');
      raf(function () {
        pop.classList.add('is-open');
      });
      focusQuietly(pop.querySelector('[data-ib-f-sort][aria-pressed="true"]') || pop.querySelector('[data-ib-f-sort]'));
    },

    closeSortPop: function (restoreFocus) {
      if (!this.sortOpen) return;
      var pop = this.root.querySelector('[data-ib-f-sortpop]');
      var btn = this.root.querySelector('[data-ib-f-open="sort"]');
      this.sortOpen = false;
      if (pop) {
        pop.classList.remove('is-open');
        pop.hidden = true;
      }
      if (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('is-open');
        if (restoreFocus) focusQuietly(btn);
      }
    },

    positionSortPop: function () {
      if (!this.sortOpen || !this.inner) return;
      var pop = this.root.querySelector('[data-ib-f-sortpop]');
      var btn = this.root.querySelector('[data-ib-f-open="sort"]');
      if (!pop || !btn) return;
      var inner = this.inner.getBoundingClientRect();
      var r = btn.getBoundingClientRect();
      var width = pop.offsetWidth || 240;
      var pad = parseFloat(window.getComputedStyle(this.inner).paddingLeft) || 0;
      var left = Math.max(pad, Math.min(r.right - inner.left - width, inner.width - pad - width));
      pop.style.left = Math.round(left) + 'px';
      pop.style.top = Math.round(r.bottom - inner.top + 8) + 'px';
    },

    /* ---------------- sheets ---------------- */

    sheetEl: function (name) {
      return findByAttr(this.root, '[data-ib-f-sheet]', 'data-ib-f-sheet', name);
    },

    isSheetLive: function (name) {
      var dialog = this.sheetEl(name);
      return Boolean(dialog && dialog.open && !dialog.classList.contains('is-closing'));
    },

    openSheet: function (name, dialogEl) {
      var dialog = dialogEl || this.sheetEl(name);
      if (!dialog || dialog.open) return;
      this.closePop(false);
      this.closeSortPop(false);
      this.lastFocus = document.activeElement;
      this.sheetSession = { pushed: false };

      var pending = false;
      // A filter set a moment ago (a rail switch, a chip, a popover pick)
      // whose render has not landed: the dialog still shows the page before
      // it, and a pick or Apply made there would quietly undo it.
      var ahead = !sameParams(this.latest, this.committed);
      if (name === 'filters') {
        this.sheetStaged = this.isMobile();
        this.sheetCleared = false;
      }
      if (name === 'filters' && this.sheetStaged) {
        var body = dialog.querySelector('[data-ib-f-sheet-body]');
        var apply = dialog.querySelector('[data-ib-f-apply]');
        this.sheetSnapshot = {
          body: body ? body.innerHTML : '',
          label: apply ? apply.innerHTML : '',
          disabled: apply ? apply.disabled : false
        };
        // A new draft has no count on the way yet (scheduleFacets says so).
        if (apply) apply.classList.remove('is-pending');
        this.draftRev += 1;
        if (ahead) {
          this.syncSheet(dialog, this.latest);
          pending = true;
        }
      } else if (ahead) {
        // The desktop panel and the Brand drawer apply instantly, from their
        // own ticks, so they catch up the same way.
        this.syncSheet(dialog, this.latest);
      }
      if (name === 'filters') this.syncSheetClear(dialog);
      dialog.classList.toggle('is-instant', !this.isStagedDialog(dialog));

      dialog.classList.remove('is-closing');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      this.paintAllRanges();
      if (pending) this.scheduleFacets();
      var self = this;
      raf(function () {
        self.buildAzRails();
        raf(function () {
          dialog.classList.add('is-open');
        });
      });
    },

    closeSheet: function (dialog, opts) {
      if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
      opts = opts || {};
      var self = this;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');

      var staged = this.isStagedDialog(dialog);
      var restore = Boolean(opts.restore && staged);
      if (restore) {
        this.draftRev += 1;
        clearTimeout(this.facetsTimer);
        if (this.facetsCtrl) this.facetsCtrl.abort();
      } else if (staged) {
        // Applied: the draft is now the state, and its render replaces the sheet.
        this.sheetSnapshot = null;
      }

      setTimeout(function () {
        dialog.classList.remove('is-closing');
        if (dialog.open) {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        }
        if (dialog.getAttribute('data-ib-f-sheet') === 'filters') self.sheetStaged = null;
        if (restore) self.restoreSheet(dialog);
        // The Filters button steps aside on a render with no filter UI; the
        // empty state's Clear all is the way on from there.
        var back = self.lastFocus;
        if (back && back.hidden) back = self.root.querySelector('[data-ib-f-results] [data-ib-f-clear-all]');
        // Each pick in the Brand drawer re-draws the rail, so the pill that
        // opened it is a new node by now.
        if (back && !document.contains(back) && dialog.hasAttribute('data-ib-f-drawer')) {
          back = self.pillFor(dialog.getAttribute('data-ib-f-drawer'));
        }
        if (back && document.contains(back)) focusQuietly(back);
        self.lastFocus = null;
      }, this.reduced.matches ? 0 : SHEET_CLOSE_MS);
    },

    // Put the sheet back as the newest render left it (swapRegions keeps the
    // snapshot current), then redo what the markup alone does not carry: the
    // region pass (the brand map can resolve while the sheet is open), the
    // list search and the A-Z rails.
    restoreSheet: function (dialog) {
      var self = this;
      var snap = this.sheetSnapshot;
      if (!snap) return;
      var body = dialog.querySelector('[data-ib-f-sheet-body]');
      var apply = dialog.querySelector('[data-ib-f-apply]');
      if (body) body.innerHTML = snap.body;
      if (apply) {
        apply.innerHTML = snap.label;
        apply.disabled = snap.disabled;
        apply.classList.remove('is-pending');
      }
      this.sheetSnapshot = null;
      if (body) {
        this.applyRegions(body);
        toArray(body.querySelectorAll('[data-ib-f-group]')).forEach(function (group) {
          self.filterGroup(group);
        });
      }
      this.buildAzRails();
      this.paintAllRanges();
    },

    // The sheet's own DOM is the draft. It replaces only what the sheet has a
    // control for (see draftFrom); after "Clear all" it starts from nothing.
    draftParams: function (dialog) {
      var self = this;
      var body = dialog.querySelector('[data-ib-f-sheet-body]') || dialog;
      var shown = [];
      var picked = [];
      var names = [];

      toArray(body.querySelectorAll('[data-ib-f-group]')).forEach(function (group) {
        if (group.getAttribute('data-ib-f-kind') === 'price') {
          var p = self.priceParts(group);
          var minName = group.getAttribute('data-ib-f-min-name');
          var maxName = group.getAttribute('data-ib-f-max-name');
          var lo = p.fieldLo ? p.fieldLo.value : '';
          var hi = p.fieldHi ? p.fieldHi.value : '';
          var live = self.sheetCleared ? new URLSearchParams() : self.latest;
          names.push(minName, maxName);
          // A range the sheet did not touch stays exactly as it is. Tidying it
          // here would drop a max at or above today's top price, and other
          // staged picks can bring dearer products back.
          if (cleanPrice(lo) === cleanPrice(live.get(minName)) && cleanPrice(hi) === cleanPrice(live.get(maxName))) {
            [minName, maxName].forEach(function (name) {
              if (live.get(name)) picked.push({ name: name, value: live.get(name) });
            });
            return;
          }
          picked = picked.concat(priceEntries(minName, maxName, lo, hi, p.max || null, live.get(maxName)));
          return;
        }
        toArray(group.querySelectorAll('[data-ib-f-key]')).forEach(function (row) {
          var values = valuesOf(row);
          var input = row.querySelector('[data-ib-f-opt]');
          shown = shown.concat(values);
          if (input && input.checked) picked = picked.concat(values);
        });
      });

      toArray(body.querySelectorAll('[data-ib-f-toggle]')).forEach(function (toggle) {
        var values = valuesOf(toggle);
        shown = shown.concat(values);
        if (toggle.getAttribute('aria-checked') === 'true') picked = picked.concat(values);
      });

      return draftFrom(this.sheetCleared ? clearAll(this.latest) : this.latest, shown, picked, names);
    },

    // Tick the sheet's controls to match `params`.
    syncSheet: function (dialog, params) {
      var self = this;
      var body = dialog.querySelector('[data-ib-f-sheet-body]') || dialog;
      var have = {};
      entriesOf(params).forEach(function (entry) {
        have[entryId(entry[0], entry[1])] = true;
      });
      var isPicked = function (scope) {
        return valuesOf(scope).some(function (entry) {
          return have[entryId(entry.name, entry.value)] === true;
        });
      };

      toArray(body.querySelectorAll('[data-ib-f-key]')).forEach(function (row) {
        var input = row.querySelector('[data-ib-f-opt]');
        if (!input) return;
        input.checked = isPicked(row);
        if (!input.checked) return;
        row.classList.remove('is-useless');
        var acc = row.closest('[data-ib-f-acc]');
        if (acc) acc.hidden = false;
      });
      toArray(body.querySelectorAll('[data-ib-f-toggle]')).forEach(function (toggle) {
        var on = isPicked(toggle);
        toggle.setAttribute('aria-checked', on ? 'true' : 'false');
        if (on) toggle.hidden = false;
      });
      toArray(body.querySelectorAll('[data-ib-f-kind="price"]')).forEach(function (group) {
        var p = self.priceParts(group);
        if (p.fieldLo) p.fieldLo.value = params.get(group.getAttribute('data-ib-f-min-name')) || '';
        if (p.fieldHi) p.fieldHi.value = params.get(group.getAttribute('data-ib-f-max-name')) || '';
        self.syncRangeFromFields(group);
      });
    },

    syncSheetClear: function (dialog) {
      var clear = dialog.querySelector('[data-ib-f-sheet-clear]');
      if (clear) clear.disabled = countActive(this.draftParams(dialog)) === 0;
    },

    clearSheet: function () {
      var dialog = this.sheetEl('filters');
      if (!dialog) return;
      var self = this;
      toArray(dialog.querySelectorAll('[data-ib-f-opt]')).forEach(function (input) {
        input.checked = false;
      });
      toArray(dialog.querySelectorAll('[data-ib-f-toggle]')).forEach(function (toggle) {
        toggle.setAttribute('aria-checked', 'false');
      });
      toArray(dialog.querySelectorAll('[data-ib-f-kind="price"]')).forEach(function (group) {
        self.resetPrice(group);
      });
      if (this.isStagedDialog(dialog)) {
        // Everything, including filters the sheet has no control for.
        this.sheetCleared = true;
        this.scheduleFacets();
      } else {
        this.commit(clearAll(this.latest), this.sheetMeta());
      }
    },

    applySheet: function () {
      var dialog = this.sheetEl('filters');
      if (!dialog) return;
      if (!this.isStagedDialog(dialog)) {
        // Instant panel: every pick is already applied; the button is "done".
        this.closeSheet(dialog, { restore: false });
        return;
      }
      var params = this.draftParams(dialog);
      this.draftRev += 1;
      clearTimeout(this.facetsTimer);
      if (this.facetsCtrl) this.facetsCtrl.abort();
      this.closeSheet(dialog, { restore: false });
      this.commit(params, { source: 'sheet' });
    },

    scheduleFacets: function () {
      var dialog = this.sheetEl('filters');
      if (!dialog) return;
      var self = this;
      this.draftRev += 1;
      var rev = this.draftRev;
      var apply = dialog.querySelector('[data-ib-f-apply]');
      if (apply) apply.classList.add('is-pending');
      this.syncSheetClear(dialog);
      clearTimeout(this.facetsTimer);
      this.facetsTimer = setTimeout(function () {
        self.fetchFacets(dialog, rev);
      }, FACETS_DEBOUNCE_MS);
    },

    fetchFacets: function (dialog, rev) {
      var self = this;
      var apply = dialog.querySelector('[data-ib-f-apply]');
      var params = this.draftParams(dialog);
      if (this.facetsCtrl) this.facetsCtrl.abort();
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      this.facetsCtrl = ctrl;

      var fail = function (err) {
        if (rev !== self.draftRev || (err && err.name === 'AbortError')) return;
        if (apply) {
          apply.textContent = self.root.getAttribute('data-ib-f-str-show') || apply.textContent;
          apply.disabled = false;
          apply.classList.remove('is-pending');
        }
      };

      var request;
      try {
        request = window.fetch(sectionUrl(window.location.pathname, params, FACETS_SECTION), {
          credentials: 'same-origin',
          signal: ctrl ? ctrl.signal : undefined
        });
      } catch (err) {
        fail(err);
        return;
      }

      request
        .then(function (res) {
          if (!res.ok) throw new Error('ib-filters: facets HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (rev !== self.draftRev || !dialog.open) return;
          var facets = parseHtml(html).querySelector('[data-ib-f-facets]');
          if (!facets) throw new Error('ib-filters: no facets');
          self.patchSheet(dialog, facets);
          var total = parseInt(facets.getAttribute('data-total'), 10) || 0;
          if (apply) {
            apply.textContent = facets.getAttribute('data-show-label') || apply.textContent;
            apply.disabled = total === 0;
            apply.classList.remove('is-pending');
          }
        })
        .catch(fail);
    },

    patchSheet: function (dialog, facets) {
      var self = this;
      var body = dialog.querySelector('[data-ib-f-sheet-body]');
      if (!body) return;
      // A draft nothing matches comes back with no groups (Shopify sends no
      // values). The sheet stays as it is, so the pick that emptied it, a
      // price included, can be undone there.
      if (!facets.querySelector('[data-ib-f-acc], [data-ib-f-toggle]')) return;

      var quick = body.querySelector('[data-ib-f-quick]');
      var seenToggles = {};
      toArray(facets.querySelectorAll('[data-ib-f-toggle]')).forEach(function (next) {
        var key = next.getAttribute('data-ib-f-toggle');
        seenToggles[key] = true;
        var toggle = findByAttr(body, '[data-ib-f-toggle]', 'data-ib-f-toggle', key);
        if (!toggle) {
          if (quick) quick.appendChild(document.importNode(next, true));
          return;
        }
        // A one-row switch can stand for another value now (16oz became 8oz
        // after a Brand pick). An unpicked one takes the new label and value.
        if (toggle.getAttribute('aria-checked') !== 'true' && !sameValues(toggle, next)) {
          var fresh = document.importNode(next, true);
          toggle.parentNode.replaceChild(fresh, toggle);
          toggle = fresh;
        }
        toggle.hidden = next.hasAttribute('hidden') && toggle.getAttribute('aria-checked') !== 'true';
      });
      toArray(body.querySelectorAll('[data-ib-f-toggle]')).forEach(function (toggle) {
        if (!seenToggles[toggle.getAttribute('data-ib-f-toggle')] && toggle.getAttribute('aria-checked') !== 'true') {
          toggle.hidden = true;
        }
      });

      var seenAccs = {};
      var prev = null;
      toArray(facets.querySelectorAll('[data-ib-f-acc]')).forEach(function (next) {
        var key = next.getAttribute('data-ib-f-acc');
        seenAccs[key] = true;
        var acc = findByAttr(body, '[data-ib-f-acc]', 'data-ib-f-acc', key);
        if (!acc) {
          acc = document.importNode(next, true);
          // Server order: after the group before it, or ahead of every other
          // group when it is the first (the quick switches stay on top).
          body.insertBefore(acc, prev ? prev.nextSibling : body.querySelector('[data-ib-f-acc]'));
          self.applyRegions(acc);
          prev = acc;
          return;
        }
        var ticked = Boolean(acc.querySelector('[data-ib-f-opt]:checked'));
        acc.hidden = next.hasAttribute('hidden') && !ticked;
        var meta = acc.querySelector('[data-ib-f-meta]');
        var nextMeta = next.querySelector('[data-ib-f-meta]');
        if (meta && nextMeta) meta.textContent = nextMeta.textContent;

        var group = acc.querySelector('[data-ib-f-group]');
        var nextGroup = next.querySelector('[data-ib-f-group]');
        if (group && nextGroup && group.getAttribute('data-ib-f-kind') === 'list' && nextGroup.getAttribute('data-ib-f-kind') === 'list') {
          patchList(group.querySelector('[data-ib-f-opts]'), nextGroup.querySelector('[data-ib-f-opts]'), {
            syncChecked: false
          });
          self.applyRegions(group);
          self.filterGroup(group);
          if (group.hasAttribute('data-ib-f-az')) self.buildAzRail(group);
        }
        prev = acc;
      });
      toArray(body.querySelectorAll('[data-ib-f-acc]')).forEach(function (acc) {
        if (!seenAccs[acc.getAttribute('data-ib-f-acc')] && !acc.querySelector('[data-ib-f-opt]:checked')) acc.hidden = true;
      });
    },

    /* ---------------- commit / render ---------------- */

    commit: function (params, meta) {
      meta = meta || {};
      if (!meta.force && sameParams(params, this.latest)) return null;
      // Only the open popover's own changes keep it open; anything else
      // (a toggle, chip, sort reached by keyboard) would leave it stale.
      if (meta.source !== 'pop') this.closePop(false);
      this.latest = params;
      // The grid is about to change, so a Load more still in flight belongs
      // to a result set that is on its way out.
      this.resultsRev += 1;
      this.cancelMore();
      if (this.moreObserver) this.moreObserver.disconnect();
      this.cancelSuggestions();
      this.endScrollRestore();
      var url = buildUrl(window.location.pathname, params);
      this.setLoading(true);
      return this.renderer.run(url, sectionUrl(window.location.pathname, params, this.sectionId), meta);
    },

    // The fallback when a render fails (a challenge page, a 5xx). After Back
    // the URL is already current, and a popover's later picks replace its
    // entry, so those load in place: assign would push a duplicate entry and
    // wipe Forward.
    navigate: function (url, replace) {
      if (replace) window.location.replace(url);
      else window.location.assign(url);
    },

    setLoading: function (on) {
      var root = this.root;
      clearTimeout(this.loadingTimer);
      if (on) {
        this.loadingTimer = setTimeout(function () {
          root.classList.add('is-loading');
        }, LOADING_DELAY_MS);
      } else {
        root.classList.remove('is-loading');
      }
    },

    apply: function (html, url, meta) {
      var doc = parseHtml(html);
      var next = doc.querySelector('[data-ib-filters]');
      var nextResults = doc.querySelector('[data-ib-f-results]');
      var results = this.root.querySelector('[data-ib-f-results]');
      if (!next || !nextResults || !results) throw new Error('ib-filters: incomplete section');
      // Filters appearing on a page that rendered none (an old link that
      // matched nothing, then Clear all), or a Brand drawer for a group that
      // was a switch or hidden here: there is no dialog to patch, and the pill
      // swapped in would open nothing, so throwing hands the renderer a full
      // navigation.
      var hasUi = Boolean(next.querySelector('[data-ib-f-sheet="filters"]'));
      if (hasUi && !this.sheetEl('filters')) throw new Error('ib-filters: filter UI appeared');
      if (this.missingDrawer(next)) throw new Error('ib-filters: brand drawer appeared');

      // The bar only starts hidden when there is nothing to put in it; it is
      // never hidden again by a render, so the shopper keeps sort and layout.
      var nextBar = next.querySelector('[data-ib-f-bar]');
      if (this.bar && this.bar.hidden && nextBar && !nextBar.hasAttribute('hidden')) this.bar.hidden = false;
      this.syncFiltersButton(hasUi);

      var focusKey = this.focusDescriptor();
      // What the entry being left showed, so Back can put it back.
      var leaving = { pages: this.pagesLoaded || 1, y: Math.round(window.pageYOffset || 0) };
      if (!hasUi) this.rememberLabels();
      this.swapRegions(next);
      this.patchPops(next, hasUi);
      this.patchDrawers(next);

      var imported = document.importNode(nextResults, true);
      results.parentNode.replaceChild(imported, results);

      this.root.setAttribute('data-ib-f-active', next.getAttribute('data-ib-f-active') || '0');
      this.root.setAttribute('data-ib-f-total', next.getAttribute('data-ib-f-total') || '0');
      this.pagesLoaded = this.currentPage();
      this.firstPage = this.pagesLoaded;
      this.autoMoreOff = false;
      this.enter(imported);
      this.root.classList.toggle('ib-f--has-filters', next.classList.contains('ib-f--has-filters'));

      // Decided when the entry is written, not at commit: an earlier pick
      // in the same popover may have landed since, and this one replaces it.
      var mode = historyMode(meta.source, meta.session);
      // A render of what is already on screen (a switch flipped and flipped
      // back before the first render landed) replaces its entry, so Back
      // never lands on a copy.
      if (mode === 'push' && sameParams(paramsFromUrl(url), this.committed)) mode = 'replace';
      // Only a real push gives the session an entry of its own; until then
      // its picks must not replace the entry before it.
      if (meta.session && mode === 'push') meta.session.pushed = true;
      try {
        if (mode === 'push') {
          // The entry being left keeps its Load more depth and scroll, so
          // Back to it does not drop products the shopper already loaded.
          this.stampEntry(leaving);
          window.history.pushState({ ibf: true }, '', url);
        } else if (mode === 'replace') window.history.replaceState({ ibf: true }, '', url);
      } catch (err) {
        /* history is best effort */
      }
      this.committed = paramsFromUrl(url);

      this.restoreFocus(focusKey);
      this.afterRender(imported, url, meta.restore);
    },

    // Adds to the current history entry's state without dropping other keys.
    stampEntry: function (extra) {
      var state = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      var merged = {};
      Object.keys(state).forEach(function (key) {
        merged[key] = state[key];
      });
      Object.keys(extra).forEach(function (key) {
        merged[key] = extra[key];
      });
      merged.ibf = true;
      window.history.replaceState(merged, '');
    },

    missingDrawer: function (next) {
      var root = this.root;
      return toArray(next.querySelectorAll('[data-ib-f-drawer]')).some(function (drawer) {
        return !findByAttr(root, '[data-ib-f-drawer]', 'data-ib-f-drawer', drawer.getAttribute('data-ib-f-drawer'));
      });
    },

    // A render with no filter UI (zero results: Shopify then reports no filter
    // values at all) carries no badge, sheet or Show button to swap in, so the
    // old ones would stay behind. Match a fresh load of the same URL instead:
    // the Filters button steps aside and its sheet closes. The empty state
    // offers Clear all and one-tap removals from there.
    syncFiltersButton: function (hasUi) {
      var btn = this.root.querySelector('[data-ib-f-open="filters"]');
      if (btn) btn.hidden = !hasUi;
      if (hasUi) return;
      var badge = this.root.querySelector('[data-ib-f-swap="badge"]');
      if (badge) badge.textContent = '';
      var sheet = this.sheetEl('filters');
      if (sheet && sheet.open) this.closeSheet(sheet, { restore: true });
    },

    focusDescriptor: function () {
      var active = document.activeElement;
      if (!active || !this.root.contains(active)) return null;
      if (this.openPopEl && this.openPopEl.contains(active)) return null;
      for (var i = 0; i < FOCUS_KEYS.length; i++) {
        var holder = active.closest('[' + FOCUS_KEYS[i] + ']');
        if (holder) return { attr: FOCUS_KEYS[i], value: holder.getAttribute(FOCUS_KEYS[i]), inSheet: this.inSheet(holder) };
      }
      if (active.closest('[data-ib-f-chip], [data-ib-f-clear-all]')) return { attr: 'chip' };
      return null;
    },

    restoreFocus: function (desc) {
      if (!desc) return;
      if (document.activeElement && this.root.contains(document.activeElement)) return;
      var target = null;
      if (desc.attr === 'chip') {
        // A phone's bar shows no chips, only the rail's compact Clear.
        target = this.root.querySelector(this.isMobile() ? '[data-ib-f-rail] [data-ib-f-clear-all]' : '.ib-f-chips [data-ib-f-chip]');
      } else {
        var self = this;
        toArray(this.root.querySelectorAll('[' + desc.attr + ']')).some(function (el) {
          if (el.getAttribute(desc.attr) === desc.value && self.inSheet(el) === desc.inSheet) {
            target = el;
            return true;
          }
          return false;
        });
      }
      // Gone or hidden (a zero-result render hides the Filters button and
      // every switch): the Filters button if it shows, else the empty
      // state's Clear all.
      if (!target || target.hidden) {
        var btn = this.root.querySelector('[data-ib-f-open="filters"]');
        target = btn && !btn.hidden ? btn : this.root.querySelector('[data-ib-f-results] [data-ib-f-clear-all]');
      }
      if (target) focusQuietly(target);
    },

    swapRegions: function (next) {
      var self = this;
      SWAP_REGIONS.forEach(function (name) {
        var selector = '[data-ib-f-swap="' + name + '"]';
        var current = toArray(self.root.querySelectorAll(selector));
        var incoming = toArray(next.querySelectorAll(selector));
        current.forEach(function (el, i) {
          if (!incoming[i]) return;
          // A draft that is later discarded falls back to this render, not to
          // the one the sheet opened on.
          if (name === 'sheet' && self.sheetSnapshot) self.sheetSnapshot.body = incoming[i].innerHTML;
          if (name === 'apply' && self.sheetSnapshot) {
            self.sheetSnapshot.label = incoming[i].innerHTML;
            self.sheetSnapshot.disabled = incoming[i].hasAttribute('disabled');
          }
          if ((name === 'sheet' || name === 'apply') && self.isSheetLive('filters')) {
            var sheet = self.sheetEl('filters');
            if (self.isStagedDialog(sheet)) return;
            if (name === 'sheet') {
              self.patchSheet(sheet, incoming[i]);
              return;
            }
          }
          el.innerHTML = incoming[i].innerHTML;
          if (name === 'apply') {
            el.disabled = incoming[i].hasAttribute('disabled');
            // Show tapped before a staged count landed: this render is the answer.
            el.classList.remove('is-pending');
          }
        });
      });
      if (this.sortOpen) {
        var btn = this.root.querySelector('[data-ib-f-open="sort"]');
        if (btn) btn.setAttribute('aria-expanded', 'true');
      }
    },

    // The Brand drawer: patched in place while open (focus, scroll and search
    // survive), replaced wholesale while closed.
    patchDrawers: function (next) {
      var self = this;
      toArray(this.root.querySelectorAll('[data-ib-f-drawer]')).forEach(function (dialog) {
        var incoming = findByAttr(next, '[data-ib-f-drawer]', 'data-ib-f-drawer', dialog.getAttribute('data-ib-f-drawer'));
        if (!incoming) {
          // No drawer to copy (a render with no filter UI); its count is the
          // bar's, so it still moves.
          var count = dialog.querySelector('[data-ib-f-drawer-count]');
          var label = next.querySelector('[data-ib-f-swap="count"]');
          if (count && label) count.textContent = label.textContent;
          return;
        }
        [['[data-ib-f-drawer-head]', 'html'], ['[data-ib-f-drawer-count]', 'text']].forEach(function (pair) {
          var el = dialog.querySelector(pair[0]);
          var from = incoming.querySelector(pair[0]);
          if (!el || !from) return;
          if (pair[1] === 'html') el.innerHTML = from.innerHTML;
          else el.textContent = from.textContent;
        });
        var body = dialog.querySelector('[data-ib-f-drawer-body]');
        var nextBody = incoming.querySelector('[data-ib-f-drawer-body]');
        if (!body || !nextBody) return;
        // Closing counts as closed: Back closes the drawer and re-renders in
        // the same moment, and ticks patched in then would outlive the URL.
        if (!dialog.open || dialog.classList.contains('is-closing')) {
          body.innerHTML = nextBody.innerHTML;
          return;
        }
        var group = body.querySelector('[data-ib-f-group]');
        var nextGroup = nextBody.querySelector('[data-ib-f-group]');
        if (!group || !nextGroup) return;
        patchList(group.querySelector('[data-ib-f-opts]'), nextGroup.querySelector('[data-ib-f-opts]'), { syncChecked: false });
        self.applyRegions(group);
        self.filterGroup(group);
        if (group.hasAttribute('data-ib-f-az')) self.buildAzRail(group);
      });
    },

    patchPops: function (next, hasUi) {
      var current = this.root.querySelector('[data-ib-f-pops]');
      var incoming = next.querySelector('[data-ib-f-pops]');
      if (!current || !incoming) return;

      var key = this.openPopKey;
      var openEl = this.openPopEl;
      // A render with no filter UI (zero results) has no popovers at all. The
      // open one stays as it is, so the change that emptied the grid (a price
      // nothing matches) can be undone where it was made; the next render
      // with filters patches it as usual.
      if (openEl && !hasUi) {
        toArray(current.children).forEach(function (child) {
          if (child !== openEl) current.removeChild(child);
        });
        return;
      }
      var nextOpen = key ? findByAttr(incoming, '[data-ib-f-pop]', 'data-ib-f-pop', key) : null;
      if (!openEl || !nextOpen) {
        if (openEl) this.closePop(false);
        current.innerHTML = incoming.innerHTML;
        return;
      }

      toArray(current.children).forEach(function (child) {
        if (child !== openEl) current.removeChild(child);
      });
      toArray(incoming.children).forEach(function (child) {
        if (child.getAttribute('data-ib-f-pop') === key) return;
        current.appendChild(document.importNode(child, true));
      });

      var head = openEl.querySelector('[data-ib-f-pop-head]');
      var nextHead = nextOpen.querySelector('[data-ib-f-pop-head]');
      if (head && nextHead) {
        var headHadFocus = head.contains(document.activeElement);
        head.innerHTML = nextHead.innerHTML;
        if (headHadFocus) focusQuietly(this.firstItem(openEl));
      }

      var group = openEl.querySelector('[data-ib-f-group]');
      var nextGroup = nextOpen.querySelector('[data-ib-f-group]');
      if (group && nextGroup && group.getAttribute('data-ib-f-kind') === 'list') {
        patchList(group.querySelector('[data-ib-f-opts]'), nextGroup.querySelector('[data-ib-f-opts]'), { syncChecked: true });
        this.applyRegions(group);
        this.filterGroup(group);
      }

      var pill = this.pillFor(key);
      if (pill) {
        pill.setAttribute('aria-expanded', 'true');
        pill.classList.add('is-open');
      }
      this.positionPop();
    },

    // `restore` is what a history entry showed when it was left (Back and
    // Forward): its Load more pages and scroll replace the jump to the top.
    afterRender: function (results, url, restore) {
      var self = this;

      if (this.view) {
        var grid = results.querySelector('.collection-grid');
        if (grid) grid.setAttribute('data-view', this.view);
      }

      this.stripSectionIds(results);

      var countEl = this.root.querySelector('[data-ib-f-swap="count"]');
      var label = countEl ? countEl.textContent.replace(/^\s+|\s+$/g, '') : '';
      if (label) {
        toArray(document.querySelectorAll('[data-collection-count]')).forEach(function (el) {
          el.textContent = label;
        });
      }

      this.syncFilteredBlocks(parseInt(this.root.getAttribute('data-ib-f-active'), 10) || 0);

      try {
        if (window.CollectionATC && typeof window.CollectionATC.rebind === 'function') quiet(window.CollectionATC.rebind(results, { fromState: true }));
      } catch (err) {
        /* quick-add degrades to Choose options, which is document-delegated */
      }
      this.bindSwatches([results]);
      try {
        if (window.theme && typeof theme.reinitProductGridItem === 'function') theme.reinitProductGridItem();
      } catch (err) {
        /* reviews badges only */
      }

      this.paintAllRanges();
      this.updateRailFades();
      this.applyRegions(this.root);
      this.buildAzRails();
      this.fillSuggestions();
      if (this.isSheetLive('filters')) this.syncSheetClear(this.sheetEl('filters'));
      document.dispatchEvent(new CustomEvent('ib:filters:rendered', { detail: { url: url, sectionId: this.sectionId } }));

      var live = this.root.querySelector('[data-ib-f-live]');
      if (live && label) {
        live.textContent = '';
        setTimeout(function () {
          live.textContent = label;
        }, 60);
      }

      if (restore && restore.pages > this.pagesLoaded) this.restorePages(restore.pages, restore.y);
      else if (restore) window.scrollTo(0, restore.y);
      else this.scrollToResults(results);
      this.watchMore();
      raf(function () {
        self.syncStuck();
        // A new count label can shift the Sort button under an open menu.
        self.positionSortPop();
      });
    },

    scrollToResults: function (results) {
      if (!this.bar) return;
      var barBottom = this.bar.getBoundingClientRect().bottom;
      var top = results.getBoundingClientRect().top;
      if (top >= barBottom - 1) return;
      var y = window.pageYOffset + top - barBottom - 8;
      try {
        window.scrollTo({ top: Math.max(0, y), behavior: this.reduced.matches ? 'auto' : 'smooth' });
      } catch (err) {
        window.scrollTo(0, Math.max(0, y));
      }
    },

    /* ---------------- small helpers ---------------- */

    // Description and subcollections step aside while filtered; the compact
    // title steps in when the description carried the page <h1>.
    syncFilteredBlocks: function (active) {
      if (!this.page) return;
      toArray(this.page.querySelectorAll('[data-ib-f-hide-when-filtered]')).forEach(function (el) {
        el.hidden = active > 0;
      });
      toArray(this.page.querySelectorAll('[data-ib-f-show-when-filtered]')).forEach(function (el) {
        el.hidden = active === 0;
      });
    },

    currentPage: function () {
      var more = this.root.querySelector('[data-ib-f-more]');
      return (more && parseInt(more.getAttribute('data-page'), 10)) || 1;
    },

    track: function (type, data) {
      try {
        if (window.IBCartTelemetry && typeof window.IBCartTelemetry.track === 'function') {
          window.IBCartTelemetry.track(type, data);
        }
      } catch (err) {
        /* telemetry never blocks filtering */
      }
    },

    // One applied render or Load more page, with the result it landed on.
    trackApply: function (source, ms, cached) {
      this.track('filter:apply', {
        src: source,
        n: parseInt(this.root.getAttribute('data-ib-f-active'), 10) || 0,
        total: parseInt(this.root.getAttribute('data-ib-f-total'), 10) || 0,
        ms: Math.round(ms || 0),
        cached: cached
      });
    },

    stripSectionIds: function (scope) {
      toArray(scope.querySelectorAll('.pagination a[href], [data-ib-f-more] a[href]')).forEach(function (link) {
        var href = link.getAttribute('href');
        if (href.indexOf('section_id=') === -1) return;
        link.setAttribute('href', href.replace(/([?&])section_id=[^&#]*&?/, '$1').replace(/[?&]$/, ''));
      });
    },

    // A short fade-and-rise on freshly rendered cards; CSS skips it under
    // prefers-reduced-motion.
    enter: function (el) {
      if (!el || this.reduced.matches) return;
      el.classList.add('is-entering');
      setTimeout(function () {
        el.classList.remove('is-entering');
      }, ENTER_MS);
    },

    /* ---------------- region-aware brand rows ---------------- */

    // Brand collections the shopper's region cannot buy from come from
    // assets/ib-brand-menu-regions.js, which hides the same brands in the
    // menu: one fetch of the map, one rule. Guests resolve to nothing.
    // Asked even with no Brand rows on the page yet, since a render or the
    // staged sheet can bring a Brand list later; the menu has already asked,
    // so this reuses its answer rather than fetching again.
    initRegions: function () {
      var self = this;
      var regions = window.IBBrandRegions;
      if (!regions || typeof regions.blocked !== 'function') return;
      regions
        .blocked()
        .then(function (blocked) {
          if (self.root.__ibf !== self || !blocked || !Object.keys(blocked).length) return;
          self.blocked = blocked;
          self.applyRegions(self.root);
          self.buildAzRails();
        })
        .catch(function () {
          /* degrade to every brand row */
        });
    },

    applyRegions: function (scope) {
      var self = this;
      var blocked = this.blocked;
      if (!blocked || !scope) return;
      // Brand rows.
      toArray(scope.querySelectorAll('[data-ib-f-handle]')).forEach(function (el) {
        var input = el.querySelector('[data-ib-f-opt]');
        var picked = Boolean(input && input.checked);
        var handles = String(el.getAttribute('data-ib-f-handle') || '').split(',');
        var hide = handles.some(function (handle) {
          return blocked[handle] === true;
        }) && !picked;
        el.classList.toggle('is-region-hidden', hide);
      });
      // A Brand list with nothing left to show steps aside: its accordion,
      // and the pill that opens its drawer or popover.
      var groups = scope.matches && scope.matches('[data-ib-f-group]') ? [scope] : toArray(scope.querySelectorAll('[data-ib-f-group]'));
      groups.forEach(function (group) {
        var rows = toArray(group.querySelectorAll('[data-ib-f-key]'));
        if (!rows.some(function (row) { return row.hasAttribute('data-ib-f-handle'); })) return;
        var empty = rows.every(function (row) {
          var input = row.querySelector('[data-ib-f-opt]');
          if (input && input.checked) return false;
          return row.classList.contains('is-region-hidden') || row.classList.contains('is-useless');
        });
        var acc = group.closest('[data-ib-f-acc]');
        if (acc) acc.classList.toggle('is-region-empty', empty);
        var holder = group.closest('[data-ib-f-drawer], [data-ib-f-pop]');
        var pill = holder && self.pillFor(holder.getAttribute('data-ib-f-drawer') || holder.getAttribute('data-ib-f-pop'));
        if (pill) pill.classList.toggle('is-region-empty', empty);
      });
    },

    /* ---------------- load more ---------------- */

    // Appends the next page to the grid it was asked from, and only that one:
    // a filter change meanwhile (commit bumps resultsRev and aborts this)
    // means the page belongs to a result set that is gone, so it is dropped,
    // and a late failure no longer navigates back to the old filters.
    loadMore: function (link, opts) {
      opts = opts || {};
      var self = this;
      if (this.moreReq || !link) return Promise.resolve(false);
      var href = link.getAttribute('href');
      var params = paramsFromUrl(href);
      var page = parseInt(params.get('page'), 10) || this.pagesLoaded + 1;
      var results = this.root.querySelector('[data-ib-f-results]');
      // The page may already be on its way, asked for on intent: that
      // request is this one, started early, and is used once.
      var pre = this.takeMorePrefetch(href, results);
      var req = {
        ctrl: pre ? pre.ctrl : typeof AbortController === 'function' ? new AbortController() : null,
        rev: this.resultsRev
      };
      var stale = function () {
        return self.moreReq !== req || req.rev !== self.resultsRev || !results || !self.root.contains(results);
      };
      this.moreReq = req;
      link.classList.add('is-busy');
      link.setAttribute('aria-busy', 'true');

      var request;
      var timer = null;
      if (pre) {
        // Its abort and timeout come with it; its markup is checked below
        // like any answer.
        request = pre.promise.then(function (html) {
          return {
            ok: true,
            status: 200,
            text: function () {
              return Promise.resolve(html);
            }
          };
        });
      } else {
        try {
          request = window.fetch(sectionUrl(window.location.pathname, params, this.sectionId), {
            credentials: 'same-origin',
            signal: req.ctrl ? req.ctrl.signal : undefined
          });
          // A request that never answers would hold the button busy and
          // swallow every later tap. Its abort is a failure like any other:
          // a tap goes to the next page as a link.
          if (req.ctrl) {
            timer = setTimeout(function () {
              req.ctrl.abort();
            }, RENDER_TIMEOUT_MS);
          }
        } catch (err) {
          request = Promise.reject(err);
        }
      }

      return Promise.resolve(request)
        .then(function (res) {
          if (stale()) return null;
          if (!res.ok) throw new Error('ib-filters: more HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (html === null || stale()) return false;
          if (html.indexOf(RESULTS_MARKER) === -1) throw new Error('ib-filters: more without results');
          var doc = parseHtml(html);
          var nextGrid = doc.querySelector('[data-ib-f-results] .collection-grid');
          var nextMore = doc.querySelector('[data-ib-f-more]');
          var grid = results.querySelector('.collection-grid');
          var more = results.querySelector('[data-ib-f-more]');
          if (!nextGrid || !grid) throw new Error('ib-filters: more without a grid');

          var added = [];
          toArray(nextGrid.children).forEach(function (child) {
            var node = document.importNode(child, true);
            grid.appendChild(node);
            self.enter(node);
            added.push(node);
          });
          var first = added[0] || null;
          if (more && nextMore) {
            // Status, meter and next link come from the new page. "Load
            // previous" stays as the page on screen had it: the new page's
            // points back at products already showing.
            var fresh = document.importNode(nextMore, true);
            var freshPrev = fresh.querySelector('[data-ib-f-more-prev]');
            var prev = more.querySelector('[data-ib-f-more-prev]');
            if (freshPrev) freshPrev.parentNode.removeChild(freshPrev);
            if (prev) fresh.appendChild(prev);
            more.parentNode.replaceChild(fresh, more);
          } else if (more) {
            more.parentNode.removeChild(more);
          }
          self.pagesLoaded = page;
          self.afterAppend(grid, added);
          // Focus follows a tap only: moving it on a page that loaded while
          // the shopper scrolled would yank the view back to the new cards.
          if (!opts.silent && !opts.auto) {
            var firstLink = first && first.querySelector('a[href]');
            if (firstLink) focusQuietly(firstLink);
          }
          if (!opts.silent) self.trackApply(opts.auto ? 'more-auto' : 'more', 0, false);
          self.watchMore();
          return true;
        })
        .catch(function () {
          if (stale()) return false;
          // A page that failed to load by itself leaves the button, which
          // goes to the next page as a link; the shopper didn't ask to leave.
          if (opts.auto) self.stopAutoMore();
          else if (!opts.silent) self.navigate(href);
          return false;
        })
        .then(function (result) {
          clearTimeout(timer);
          if (self.moreReq === req) self.moreReq = null;
          link.classList.remove('is-busy');
          link.removeAttribute('aria-busy');
          return result;
        });
    },

    // Watches the Load more strip, which every page replaces, while the
    // result set has automatic pages left. The button stays either way: it
    // is the fallback without IntersectionObserver, on Save-Data, and for a
    // shopper who gets there first.
    watchMore: function () {
      var self = this;
      if (this.moreObserver) this.moreObserver.disconnect();
      var more = this.root.querySelector('[data-ib-f-more]');
      if (!more || !more.querySelector('[data-ib-f-more-link]') || !this.autoMoreLeft()) return;
      var Observer = window.IntersectionObserver;
      var conn = window.navigator && window.navigator.connection;
      if (typeof Observer !== 'function' || (conn && conn.saveData)) return;
      // Made again each time, so the margin follows a rotated phone.
      var margin = Math.round((window.innerHeight || 800) * AUTO_MORE_SCREENS);
      this.moreObserver = new Observer(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) self.autoMore();
      }, { rootMargin: '0px 0px ' + margin + 'px 0px' });
      this.moreObserver.observe(more);
    },

    autoMoreLeft: function () {
      return !this.autoMoreOff && this.pagesLoaded - this.firstPage < AUTO_MORE_PAGES;
    },

    autoMore: function () {
      // Back's page restore and a tap already in flight load pages of their
      // own; the strip each one lands is watched again.
      if (this.moreReq || this.restoringPages || !this.autoMoreLeft()) return;
      var link = this.root.querySelector('[data-ib-f-more-link]');
      if (link) this.loadMore(link, { auto: true });
    },

    stopAutoMore: function () {
      this.autoMoreOff = true;
      if (this.moreObserver) this.moreObserver.disconnect();
    },

    cancelMore: function () {
      var req = this.moreReq;
      this.moreReq = null;
      if (req && req.ctrl) req.ctrl.abort();
      // A page asked for on intent belongs to the same grid.
      this.endMoreHover();
      this.dropMorePrefetch();
    },

    // Asks for the page "Show more" loads before it is tapped (the intents
    // are wired in bind), so loadMore usually finds it on its way or there.
    // One request per page and result set a minute, taken once. A filter,
    // sort or results change drops it (commit calls cancelMore). One that
    // fails is never shown and never used: the tap asks for itself. Nothing
    // is asked for while a page loads (a tap, by itself, or Back putting
    // pages back) or a filter change does, during a cart security check, or
    // with Save-Data on.
    prefetchMore: function (link) {
      if (this.moreReq || !this.root.contains(link)) return;
      // A filter change still loading: this link is the old grid's.
      if (!sameParams(this.latest, this.committed)) return;
      var href = link.getAttribute('href');
      var results = this.root.querySelector('[data-ib-f-results]');
      if (this.morePrefetch && this.prefetchFits(this.morePrefetch, href, results)) return;
      var cart = window.theme && window.theme.cart;
      if (cart && typeof cart.isChallengeActive === 'function' && cart.isChallengeActive()) return;
      var conn = window.navigator && window.navigator.connection;
      if (conn && conn.saveData) return;
      this.dropMorePrefetch();

      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var pre = { href: href, rev: this.resultsRev, results: results, t: Date.now(), ctrl: ctrl, timer: null, failed: false };
      var request;
      try {
        request = window.fetch(sectionUrl(window.location.pathname, paramsFromUrl(href), this.sectionId), {
          credentials: 'same-origin',
          signal: ctrl ? ctrl.signal : undefined
        });
        // The tap's own limit, counted from when this request started.
        if (ctrl) {
          pre.timer = setTimeout(function () {
            ctrl.abort();
          }, RENDER_TIMEOUT_MS);
        }
      } catch (err) {
        request = Promise.reject(err);
      }
      // Like the render cache, only markup that passed the marker check is
      // kept: a challenge page or a 5xx is a failure.
      pre.promise = Promise.resolve(request)
        .then(function (res) {
          if (!res || !res.ok) throw new Error('ib-filters: prefetch HTTP ' + (res && res.status));
          return res.text();
        })
        .then(function (html) {
          if (!html || html.indexOf(RESULTS_MARKER) === -1) throw new Error('ib-filters: prefetch without results');
          return html;
        });
      // A failure nobody took ends here, not as an unhandled rejection.
      pre.promise.then(
        function () {
          clearTimeout(pre.timer);
        },
        function () {
          clearTimeout(pre.timer);
          pre.failed = true;
        }
      );
      this.morePrefetch = pre;
    },

    // Asked for this link's page, for the grid on screen, within the minute.
    prefetchFits: function (pre, href, results) {
      return pre.href === href && pre.rev === this.resultsRev && pre.results === results && Date.now() - pre.t <= MORE_PREFETCH_TTL_MS;
    },

    // The prefetched page for loadMore, if it fits and has not failed. The
    // slot is emptied either way, so a page is used once, and a request
    // nobody will use is stopped.
    takeMorePrefetch: function (href, results) {
      var pre = this.morePrefetch;
      if (pre && !pre.failed && this.prefetchFits(pre, href, results)) {
        this.morePrefetch = null;
        return pre;
      }
      this.dropMorePrefetch();
      return null;
    },

    dropMorePrefetch: function () {
      var pre = this.morePrefetch;
      this.morePrefetch = null;
      if (!pre) return;
      clearTimeout(pre.timer);
      if (pre.ctrl) pre.ctrl.abort();
    },

    // A mouse or pen over the button: ask once it has rested there, so a
    // pointer passing over it on the way down the page asks for nothing.
    hoverMore: function (link) {
      if (link === this.moreHover) return;
      this.endMoreHover();
      var self = this;
      this.moreHover = link;
      this.moreHoverTimer = setTimeout(function () {
        self.moreHoverTimer = null;
        self.prefetchMore(link);
      }, MORE_HOVER_MS);
    },

    endMoreHover: function () {
      clearTimeout(this.moreHoverTimer);
      this.moreHoverTimer = null;
      this.moreHover = null;
    },

    afterAppend: function (grid, added) {
      var results = this.root.querySelector('[data-ib-f-results]');
      if (this.view) grid.setAttribute('data-view', this.view);
      if (results) this.stripSectionIds(results);
      // Only the new cards: a pass over the whole grid would re-scan every
      // page already loaded and repaint a quantity the shopper is typing.
      try {
        if (window.CollectionATC && typeof window.CollectionATC.rebind === 'function') {
          (added || []).forEach(function (node) {
            if (node.querySelectorAll) quiet(window.CollectionATC.rebind(node, { fromState: true }));
          });
        }
      } catch (err) {
        /* Choose options still works: it is document-delegated */
      }
      this.bindSwatches(added || []);
      try {
        if (window.theme && typeof theme.reinitProductGridItem === 'function') theme.reinitProductGridItem();
      } catch (err) {
        /* reviews badges only */
      }
    },

    // Like stock theme.js on a layout switch: lazysizes re-measures the card
    // images on resize. Renders and Load more need no such nudge (stock's own
    // filter render sends none), and this controller's handler skips the echo.
    notifyResize: function () {
      this.ownResize = true;
      try {
        window.dispatchEvent(new Event('resize'));
      } finally {
        this.ownResize = false;
      }
    },

    // Swatch hover on the cards a render or Load more just added, wired once
    // each. theme.sections.reinit would do it by re-running theme.Collection,
    // which binds every swatch already on the page again on every pass.
    bindSwatches: function (nodes) {
      nodes.forEach(function (node) {
        if (!node.querySelectorAll) return;
        toArray(node.querySelectorAll('.color-swatch--with-image')).forEach(function (swatch) {
          var show = function () {
            showSwatchImage(swatch);
          };
          swatch.addEventListener('mouseenter', show);
          swatch.addEventListener('touchstart', show, { passive: true });
        });
      });
    },

    /* ---------------- scroll restoration ---------------- */

    scrollKey: function () {
      return SCROLL_KEY + window.location.pathname + window.location.search;
    },

    saveScroll: function () {
      try {
        window.sessionStorage.setItem(this.scrollKey(), JSON.stringify({
          y: Math.round(window.pageYOffset || 0),
          pages: this.pagesLoaded || 1,
          t: Date.now()
        }));
      } catch (err) {
        /* private mode: nothing to restore */
      }
    },

    // Back from a product page: reload the extra "Load more" pages the
    // shopper had open, then put them back where they were.
    restoreScroll: function () {
      var nav = window.performance && window.performance.getEntriesByType ? window.performance.getEntriesByType('navigation')[0] : null;
      var type = nav ? nav.type : '';
      if (type !== 'back_forward') return;
      var saved = null;
      try {
        saved = JSON.parse(window.sessionStorage.getItem(this.scrollKey()) || 'null');
      } catch (err) {
        saved = null;
      }
      if (!saved || Date.now() - saved.t > SCROLL_TTL_MS) return;

      var target = saved.pages || 1;
      if (target <= this.pagesLoaded) {
        if ((window.pageYOffset || 0) < 10 && saved.y > 10) window.scrollTo(0, saved.y);
        return;
      }
      this.restorePages(target, saved.y);
    },

    // Loads the next pages until `target` are showing, then scrolls to y.
    restorePages: function (target, y) {
      var self = this;
      this.restoringPages = true;
      try {
        this.scrollModeWas = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      } catch (err) {
        /* older browsers restore on their own */
      }
      // A filter change while this runs ends it quietly (commit calls
      // endScrollRestore): that grid is gone.
      var rev = this.resultsRev;
      var finish = function () {
        self.endScrollRestore();
        window.scrollTo(0, y);
      };
      var step = function () {
        if (rev !== self.resultsRev) return;
        var link = self.root.querySelector('[data-ib-f-more-link]');
        if (!link || self.pagesLoaded >= target) {
          finish();
          return;
        }
        self.loadMore(link, { silent: true }).then(function (ok) {
          if (rev !== self.resultsRev) return;
          if (ok) step();
          else finish();
        });
      };
      step();
    },

    // The browser's own restoring is only held off while the pages reload.
    // Left 'manual', Back and Forward across filter changes would lose their
    // scroll position for the rest of the visit.
    endScrollRestore: function () {
      this.restoringPages = false;
      if (!this.scrollModeWas) return;
      try {
        window.history.scrollRestoration = this.scrollModeWas;
      } catch (err) {
        /* nothing was changed */
      }
      this.scrollModeWas = null;
    },

    /* ---------------- A-Z rail (brand list in the sheet) ---------------- */

    buildAzRails: function () {
      var self = this;
      toArray(this.root.querySelectorAll('[data-ib-f-sheet] [data-ib-f-az]')).forEach(function (group) {
        self.buildAzRail(group);
      });
    },

    visibleRows: function (group) {
      return toArray(group.querySelectorAll('[data-ib-f-key]')).filter(function (li) {
        return !li.classList.contains('is-useless') && !li.classList.contains('is-region-hidden');
      });
    },

    letterOf: function (li) {
      if (li.__ibfNorm === undefined) li.__ibfNorm = normalizeText(li.getAttribute('data-ib-f-label'));
      return letterFor(li.__ibfNorm);
    },

    // A rail only earns its place when the list is long enough to scroll past;
    // where every brand already fits it is a dead control.
    // Only measurable while the list is on screen, so: true, false, or null
    // (closed dialog or accordion; keep whatever is there and decide on open).
    railNeeded: function (group, rows) {
      if (rows.length < AZ_MIN_ROWS) return false;
      var dialog = group.closest('dialog');
      var body = group.closest('[data-ib-f-sheet-body]');
      var list = group.querySelector('[data-ib-f-opts]');
      if (!dialog || !dialog.open || !body || !list || !list.offsetHeight) return null;
      return list.offsetHeight > body.clientHeight * AZ_SCROLL_RATIO;
    },

    buildAzRail: function (group) {
      var self = this;
      var rows = this.visibleRows(group);
      var rail = group.querySelector('[data-ib-f-az-rail]');
      var needed = this.railNeeded(group, rows);
      if (needed === false) {
        if (rail) rail.parentNode.removeChild(rail);
        group.classList.remove('has-az');
        return;
      }
      if (needed === null && !rail) return;
      if (!rail) {
        rail = document.createElement('nav');
        rail.className = 'ib-f-az';
        rail.setAttribute('data-ib-f-az-rail', '');
        rail.setAttribute('aria-label', this.root.getAttribute('data-ib-f-str-az') || 'Jump to letter');
        group.appendChild(rail);
      }
      var present = {};
      rows.forEach(function (li) {
        present[self.letterOf(li)] = true;
      });
      rail.innerHTML = '';
      rail.__bubble = null;
      AZ_LETTERS.forEach(function (letter) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ib-f-az__btn';
        btn.textContent = letter;
        btn.setAttribute('data-ib-f-az-letter', letter);
        if (!present[letter]) btn.disabled = true;
        rail.appendChild(btn);
      });
      group.classList.add('has-az');
      this.syncAzCurrent(group);
    },

    // Scroll-spy: the letter of the first row showing at the top of the sheet.
    syncAzCurrent: function (group, forced) {
      var self = this;
      var rail = group.querySelector('[data-ib-f-az-rail]');
      var body = group.closest('[data-ib-f-sheet-body]');
      if (!rail || !body) return;
      var current = forced || null;
      if (!current) {
        var top = body.getBoundingClientRect().top;
        this.visibleRows(group).some(function (li) {
          if (li.getBoundingClientRect().bottom > top + 4) {
            current = self.letterOf(li);
            return true;
          }
          return false;
        });
      }
      toArray(rail.querySelectorAll('[data-ib-f-az-letter]')).forEach(function (btn) {
        btn.classList.toggle('is-current', btn.getAttribute('data-ib-f-az-letter') === current);
      });
    },

    // Tap: smooth scroll to the letter's first brand and flash it, so the jump
    // reads even when the list cannot scroll that far. Scrub: instant, quiet.
    jumpToLetter: function (btn, opts) {
      opts = opts || {};
      var self = this;
      var group = btn.closest('[data-ib-f-az]');
      var body = btn.closest('[data-ib-f-sheet-body]');
      if (!group || !body || btn.disabled) return;
      var letter = btn.getAttribute('data-ib-f-az-letter');
      var target = null;
      this.visibleRows(group).some(function (li) {
        if (self.letterOf(li) === letter) {
          target = li;
          return true;
        }
        return false;
      });
      if (!target) return;
      var top = target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 8;
      this.azHoldUntil = Date.now() + (opts.instant ? AZ_HOLD_SCRUB_MS : AZ_HOLD_TAP_MS);
      try {
        body.scrollTo({ top: top, behavior: opts.instant || this.reduced.matches ? 'auto' : 'smooth' });
      } catch (err) {
        body.scrollTop = top;
      }
      this.syncAzCurrent(group, letter);
      if (opts.quiet || this.reduced.matches) return;
      target.classList.remove('is-target');
      void target.offsetWidth;
      target.classList.add('is-target');
      clearTimeout(target.__ibfTarget);
      target.__ibfTarget = setTimeout(function () {
        target.classList.remove('is-target');
      }, 1200);
    },

    // Drag along the rail (touch or pen) to scrub, with a letter bubble. Probe
    // along the rail's centre line, not the finger's x: the buttons are narrow
    // and a thumb drifts inwards mid-drag (the mobile menu rail learned this).
    // Scrubbing starts after a small slop, so a plain tap stays a smooth jump.
    bindAzScrub: function () {
      var self = this;
      var drag = null;
      this.listen(this.root, 'pointerdown', function (evt) {
        var rail = evt.target && evt.target.closest ? evt.target.closest('[data-ib-f-az-rail]') : null;
        if (!rail || evt.pointerType === 'mouse') return;
        drag = { rail: rail, id: evt.pointerId, y: evt.clientY, scrubbing: false, letter: null };
      });
      this.listen(this.root, 'pointermove', function (evt) {
        if (!drag || evt.pointerId !== drag.id) return;
        if (!drag.scrubbing) {
          if (Math.abs(evt.clientY - drag.y) < AZ_SCRUB_SLOP_PX) return;
          drag.scrubbing = true;
          try {
            drag.rail.setPointerCapture(evt.pointerId);
          } catch (err) {
            /* capture is a nicety */
          }
        }
        if (evt.cancelable) evt.preventDefault();
        var r = drag.rail.getBoundingClientRect();
        var y = Math.max(r.top + 1, Math.min(r.bottom - 1, evt.clientY));
        var hit = document.elementFromPoint(r.left + r.width / 2, y);
        var btn = hit && hit.closest ? hit.closest('[data-ib-f-az-letter]') : null;
        if (btn && !btn.disabled && btn.getAttribute('data-ib-f-az-letter') !== drag.letter) {
          drag.letter = btn.getAttribute('data-ib-f-az-letter');
          self.jumpToLetter(btn, { instant: true, quiet: true });
        }
        self.showAzBubble(drag.rail, evt.clientY, drag.letter);
      });
      var end = function (evt) {
        if (!drag || evt.pointerId !== drag.id) return;
        var scrubbed = drag.scrubbing;
        var rail = drag.rail;
        drag = null;
        if (!scrubbed) return;
        self.hideAzBubble(rail);
        self.suppressAzClick = true;
        setTimeout(function () {
          self.suppressAzClick = false;
        }, 60);
      };
      this.listen(this.root, 'pointerup', end);
      this.listen(this.root, 'pointercancel', end);
    },

    showAzBubble: function (rail, y, letter) {
      if (!letter) return;
      var bubble = rail.__bubble;
      if (!bubble || !rail.contains(bubble)) {
        bubble = document.createElement('span');
        bubble.className = 'ib-f-az__bubble';
        bubble.setAttribute('aria-hidden', 'true');
        rail.appendChild(bubble);
        rail.__bubble = bubble;
      }
      var r = rail.getBoundingClientRect();
      bubble.textContent = letter;
      bubble.style.top = Math.round(Math.max(0, Math.min(r.height, y - r.top))) + 'px';
      bubble.classList.add('is-on');
    },

    hideAzBubble: function (rail) {
      if (rail && rail.__bubble) rail.__bubble.classList.remove('is-on');
    },

    /* ---------------- zero-result suggestions ---------------- */

    fillSuggestions: function () {
      var self = this;
      var box = this.root.querySelector('[data-ib-f-suggest]');
      if (!box) return;
      if (countActive(this.latest) === 0) return;
      // The server rendered the unfiltered empty state (Shopify reports no
      // filter values when the URL alone matches nothing); the URL knows
      // better, so swap to the filtered one.
      toArray(this.root.querySelectorAll('[data-ib-f-empty], [data-ib-f-empty-actions]')).forEach(function (el) {
        el.hidden = false;
      });
      toArray(this.root.querySelectorAll('[data-ib-f-empty-plain]')).forEach(function (el) {
        el.hidden = true;
      });
      this.syncFilteredBlocks(countActive(this.latest));

      var candidates = suggestionCandidates(
        toArray(this.root.querySelectorAll('.ib-f-chips [data-ib-f-chip]')).map(function (chip) {
          var label = chip.querySelector('.ib-f-chip__label');
          return {
            label: label ? label.textContent : '',
            entries: valuesOf(chip),
            price: chip.hasAttribute('data-ib-f-chip-price')
          };
        }),
        this.latest,
        this.root.getAttribute('data-ib-f-str-price') || 'Price',
        this.labelOf.bind(this)
      ).slice(0, SUGGEST_MAX);
      if (!candidates.length) return;

      this.cancelSuggestions();
      var rev = this.suggestRev;
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      this.suggestCtrl = ctrl;
      var base = this.latest;
      this.track('filter:empty', { n: countActive(base) });

      Promise.all(candidates.map(function (candidate) {
        var params = candidate.price
          ? replaceGroup(base, namesOf(candidate.entries), [])
          : removeEntries(base, candidate.entries);
        return window.fetch(sectionUrl(window.location.pathname, params, FACETS_SECTION), {
          credentials: 'same-origin',
          signal: ctrl ? ctrl.signal : undefined
        })
          .then(function (res) {
            return res.ok ? res.text() : '';
          })
          .then(function (html) {
            var facets = html ? parseHtml(html).querySelector('[data-ib-f-facets]') : null;
            if (!facets) return null;
            return {
              candidate: candidate,
              total: parseInt(facets.getAttribute('data-total'), 10) || 0,
              label: facets.getAttribute('data-count-label') || ''
            };
          })
          .catch(function () {
            return null;
          });
      })).then(function (list) {
        if (rev !== self.suggestRev || !document.contains(box)) return;
        var picks = rankSuggestions(list);
        if (!picks.length) return;
        box.innerHTML = '';
        var title = document.createElement('p');
        title.className = 'ib-f-suggest__title';
        title.textContent = self.root.getAttribute('data-ib-f-str-suggest') || '';
        box.appendChild(title);
        var wrap = document.createElement('div');
        wrap.className = 'ib-f-suggest__list';
        picks.forEach(function (pick) {
          wrap.appendChild(self.suggestionButton(pick.candidate, pick.label));
        });
        box.appendChild(wrap);
        box.hidden = false;
      });
    },

    // Suggestions belong to the empty grid they were asked for: a filter
    // change or teardown cancels any still loading.
    cancelSuggestions: function () {
      this.suggestRev += 1;
      if (this.suggestCtrl) this.suggestCtrl.abort();
      this.suggestCtrl = null;
    },

    // A render with no filter UI drops every label, and its suggestions only
    // have the URL to go on, so the labels on screen are kept first.
    rememberLabels: function () {
      var labels = this.labels;
      var add = function (scope, label) {
        label = String(label || '').replace(/^\s+|\s+$/g, '');
        if (!label) return;
        valuesOf(scope).forEach(function (entry) {
          labels[entryId(entry.name, entry.value)] = label;
        });
      };
      toArray(this.root.querySelectorAll('[data-ib-f-key]')).forEach(function (row) {
        add(row, row.getAttribute('data-ib-f-label'));
      });
      toArray(this.root.querySelectorAll('[data-ib-f-toggle], [data-ib-f-chip]')).forEach(function (el) {
        var label = el.querySelector('.ib-f-toggle__label, .ib-f-chip__label');
        add(el, label ? label.textContent : '');
      });
    },

    // An old link that matched nothing from the start has no labels on the
    // page, but In stock is always the same one.
    labelOf: function (name, value) {
      var known = this.labels[entryId(name, value)];
      if (known) return known;
      if (name === 'filter.v.availability' && value === '1') return this.root.getAttribute('data-ib-f-str-in-stock') || '';
      return '';
    },

    // Same contract as a chip (data-ib-f-chip + hidden values), so onChip
    // handles the click.
    suggestionButton: function (candidate, countLabel) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ib-f-chip ib-f-chip--suggest';
      btn.setAttribute('data-ib-f-chip', '');
      if (candidate.price) btn.setAttribute('data-ib-f-chip-price', '');
      btn.setAttribute('aria-label', candidate.label + ', ' + countLabel);
      var label = document.createElement('span');
      label.className = 'ib-f-chip__label';
      label.textContent = candidate.label;
      var count = document.createElement('span');
      count.className = 'ib-f-chip__count';
      count.textContent = countLabel;
      btn.appendChild(label);
      btn.appendChild(count);
      var icon = this.root.querySelector('.ib-f-ico--x');
      if (icon) btn.appendChild(icon.cloneNode(true));
      candidate.entries.forEach(function (entry) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.setAttribute('data-ib-f-value', '');
        input.setAttribute('name', entry.name);
        input.value = entry.value;
        btn.appendChild(input);
      });
      return btn;
    },

    /* ---------------- swipe down to close (phone sheets) ---------------- */

    bindSwipe: function (dialog) {
      var self = this;
      var panel = dialog.querySelector('.ib-f-sheet__panel');
      if (!panel) return;
      var drag = null;

      var onDown = function (evt) {
        if (!self.isMobile() || evt.pointerType === 'mouse') return;
        if (evt.target.closest('button, a, input')) return;
        drag = { id: evt.pointerId, y: evt.clientY, t: Date.now(), dy: 0 };
        panel.style.transition = 'none';
        try {
          evt.currentTarget.setPointerCapture(evt.pointerId);
        } catch (err) {
          /* capture is a nicety */
        }
      };
      var onMove = function (evt) {
        if (!drag || evt.pointerId !== drag.id) return;
        drag.dy = Math.max(0, evt.clientY - drag.y);
        panel.style.transform = 'translateY(' + drag.dy + 'px)';
      };
      var onUp = function (evt) {
        if (!drag || evt.pointerId !== drag.id) return;
        var dy = drag.dy;
        var speed = dy / Math.max(1, Date.now() - drag.t);
        drag = null;
        panel.style.transition = '';
        panel.style.transform = '';
        if (dy > SWIPE_CLOSE_PX || (dy > 40 && speed > 0.6)) {
          self.closeSheet(dialog, { restore: dialog.getAttribute('data-ib-f-sheet') === 'filters' });
        }
      };

      toArray(dialog.querySelectorAll('.ib-f-sheet__grab, .ib-f-sheet__head')).forEach(function (handle) {
        self.listen(handle, 'pointerdown', onDown);
        self.listen(handle, 'pointermove', onMove);
        self.listen(handle, 'pointerup', onUp);
        self.listen(handle, 'pointercancel', onUp);
      });
    },

    /* ---------------- sticky bar ---------------- */

    // The bar sticks to the bottom edge of the visible header, measured live.
    // That one number covers every case: a sticky header (constant), one that
    // scrolls away (goes to 0), and the compressed header (shrinks). The
    // .header-section box itself is no guide: on phones it carries 62px of
    // transparent bottom padding, and theme.config.stickyHeader is never set.
    stickyTop: function () {
      var header = document.getElementById('SiteHeader');
      if (!header) return 0;
      return Math.max(0, Math.round(header.getBoundingClientRect().bottom) - 1);
    },

    updateTop: function () {
      var top = this.stickyTop();
      if (top === this.top) return;
      this.top = top;
      this.root.style.setProperty('--ibf-top', top + 'px');
    },

    syncStuck: function () {
      if (!this.bar) return;
      var rootTop = this.root.getBoundingClientRect().top;
      var stuck = rootTop < Math.max(0, this.top) - 1;
      this.bar.classList.toggle('is-stuck', stuck);
    },

    updateRailFades: function () {
      var rail = this.rail;
      if (!rail) return;
      var max = rail.scrollWidth - rail.clientWidth;
      rail.classList.toggle('has-left', rail.scrollLeft > 2);
      rail.classList.toggle('has-right', max - rail.scrollLeft > 2);
    }
  };

  /* ------------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------------ */

  var api = {
    normalizeText: normalizeText,
    matchesQuery: matchesQuery,
    paramsFromUrl: paramsFromUrl,
    buildUrl: buildUrl,
    sectionUrl: sectionUrl,
    replaceGroup: replaceGroup,
    removeEntries: removeEntries,
    appendEntries: appendEntries,
    clearAll: clearAll,
    setSort: setSort,
    setPrice: setPrice,
    priceBounds: priceBounds,
    priceEntries: priceEntries,
    draftFrom: draftFrom,
    cleanPrice: cleanPrice,
    countActive: countActive,
    historyMode: historyMode,
    createRenderer: createRenderer,
    patchList: patchList,
    createCache: createCache,
    letterFor: letterFor,
    rankSuggestions: rankSuggestions,
    suggestionCandidates: suggestionCandidates,
    instances: [],
    boot: boot
  };

  function boot(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    if (!host || !host.querySelectorAll) return;
    toArray(host.querySelectorAll('[data-ib-filters]')).forEach(function (root) {
      if (root.__ibf) return;
      root.__ibf = new Filters(root);
      api.instances.push(root.__ibf);
    });
  }

  function teardown(scope) {
    if (!scope || !scope.querySelectorAll) return;
    toArray(scope.querySelectorAll('[data-ib-filters]')).forEach(function (root) {
      if (!root.__ibf) return;
      var instance = root.__ibf;
      instance.destroy();
      api.instances = api.instances.filter(function (it) {
        return it !== instance;
      });
    });
  }

  window.IBFilters = api;

  if (typeof document === 'undefined' || !document.addEventListener) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (evt) {
    boot(evt.target);
  });
  document.addEventListener('shopify:section:unload', function (evt) {
    teardown(evt.target);
  });
})();
